import React, { useState, useEffect } from 'react';
import { FileText, Printer, Plus, Trash2, RotateCcw, Download, Save, AlertTriangle } from 'lucide-react';
import { RATE_FIELDS, RATE_TYPES, CURRENT_RATES, normaliseRates, versionFor, latestVersion, sortVersions, ratesFor, ratesDiffer, applyCpi, versionBefore } from '../utils/invoiceRates';
import { periodGrid } from '../utils/periods';

// ── Constants ──────────────────────────────────────────────────────────────────

const COMPANY = {
  name: 'PH Bouveng Pty Ltd',
  poBox: 'PO Box 1710',
  suburb: 'Warriewood NSW 2102',
  mob: '0411424244',
  fax: '0299860965',
  abn: '12 093 039 297',
  bsb: '062155',
  acc: '10356910',
};

const DOE_RECIPIENT   = { attn: 'Elmir Slieman', dept: 'Sydney Parcel Delivery', code: 'N22000', company: 'Australia Post', address: '1a Hale St', suburb: 'Botany NSW 2019' };
const WAVE_RECIPIENT  = { attn: 'Brad Henwood',  dept: 'Sydney Parcel Delivery', code: 'N22000', company: 'Australia Post', address: '10 Ralph St', suburb: 'Alexandria NSW 2015' };
const NIGHT_RECIPIENT = { attn: 'Tom Varghese',  dept: 'Sydney Parcel Delivery', code: 'N22000', company: 'Australia Post', address: '1a Hale St', suburb: 'Botany NSW 2019' };

const DEFAULT_SCHOOLS = [
  'Crown Street Public School',
  'Bourke Street Public School',
  'Plunkett Street Public School',
  'Darlinghurst Public School',
  'Bridge Road School',
  'Forest Lodge Public School',
  'Glebe Public School',
  'RPA Hospital School',
  'Sydney Secondary College',
  'Ultimo Public school',
  'Fort St Public School',
  'Inner Sydney High School',
];

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Every rate on this page comes from the saved Rates tab (src/utils/invoiceRates.js,
// stored server-side with history) — never from code defaults or the browser.
// Night Sort / Additional Time rates USED to live per browser under this key; it is
// read once only to offer this device's values on the Rates tab, then removed, so a
// stale browser copy can never bill over the saved rates.
const LEGACY_NIGHT_RATES_KEY = 'pva_night_rates_v1';

async function ratesApi(action, payload = {}) {
  const r = await fetch('/.netlify/functions/tax-invoices-sb', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...payload }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`);
  return d;
}

// The night grid runs Sunday→Saturday (column = the night's START day), so it
// lines up 1:1 with Night Sorters → Actual times. Sunday (column 0) bills at
// the Sunday rates; Mon–Sat at the weekday rates.
const NIGHT_SUN_RATE = { staff: 'staffSun', leader: 'leaderSun', staffOT: 'staffSunOT', leaderOT: 'leaderSunOT' };
function nightSplit(vals, rates, key) {
  const sunH = Math.round(parseFloat(vals[0] || 0) * 100) / 100;
  const wkH  = Math.round(vals.slice(1).reduce((a, v) => a + parseFloat(v || 0), 0) * 100) / 100;
  const wkRate  = rates[key] || 0;
  const sunRate = rates[NIGHT_SUN_RATE[key]] != null ? rates[NIGHT_SUN_RATE[key]] : wkRate;
  return { sunH, wkH, wkRate, sunRate, due: sunH * sunRate + wkH * wkRate };
}

// ── Period generation (28-day periods, Saturday end, same cadence as RCTI) ────
// Anchor: 16 May 2026 (known period end)
const PERIOD_ANCHOR = '2026-05-16';

// Walks out from TODAY's period (periodGrid), not from the anchor: a fixed
// -24…+6 window around 16 May 2026 ran out on 31 Oct 2026, hiding the current period.
function generatePeriods() {
  return periodGrid({ back: 24, forward: 6 });
}

function addDaysStatic(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d + n);
  const yr = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const dy = String(date.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${dy}`;
}


const PERIODS = generatePeriods();

// Find the period that contains today's date
function findCurrentPeriod() {
  return PERIODS.find(p => p.current) || PERIODS[0];
}

// ── Invoice number tracking ───────────────────────────────────────────────────
// The REAL counter lives server-side (Invoice_Counter sheet — one global
// sequence shared by all invoice types AND all users/devices, since BOUVENGJ
// and BOUVENGP2 both issue invoices). localStorage is only a same-session
// fallback. The page pre-fills lastIssued+1 on load; printing records the
// number so the next invoice anywhere continues the sequence.
const INV_KEY = 'pva_invoice_numbers';

function getNextInvoiceNum(type) {
  try {
    const stored = JSON.parse(localStorage.getItem(INV_KEY) || '{}');
    return stored[type] || '';
  } catch { return ''; }
}

function saveInvoiceNum(type, num) {
  try {
    const stored = JSON.parse(localStorage.getItem(INV_KEY) || '{}');
    // Save the next number so next invoice pre-fills incremented value
    const next = parseInt(num);
    stored[type] = isNaN(next) ? num : String(next + 1);
    localStorage.setItem(INV_KEY, JSON.stringify(stored));
  } catch {}
  // Record on the shared server counter (fire-and-forget)
  const n = parseInt(num);
  if (!isNaN(n)) {
    fetch('/.netlify/functions/appsscript', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'recordInvoiceNumber', num: n }),
    }).catch(() => {});
  }
}

// Map any date inside a pay period to its canonical 28-day period end (Saturday),
// matching PERIOD_ANCHOR / RCTI cadence. The DoE & Wave forms hold the Mon–Fri
// shifted end (one day before), so this snaps them back to the true period end.
function canonPeriodEnd(iso) {
  if (!/^\d{4}-\d{2}-\d{2}/.test(iso || '')) return '';
  const day = (s) => Math.round(Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10)) / 86400000);
  const delta = day(iso) - day(PERIOD_ANCHOR);
  const end = day(PERIOD_ANCHOR) + Math.ceil(delta / 28) * 28;
  return new Date(end * 86400000).toISOString().slice(0, 10);
}

// Persist a generated invoice's totals so the Revenue "Cash Flow" view can show
// figures that match the invoice actually issued. Fire-and-forget — never blocks
// or alters printing. type ∈ {doe, wave, night}.
function saveTaxInvoiceTotal(type, form, { subtotalEx, gst, total, quantity }) {
  const periodEnd = canonPeriodEnd(form.periodEnd || form.periodStart);
  if (!periodEnd) return;
  fetch('/.netlify/functions/tax-invoices-sb', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'saveTaxInvoice', type, periodEnd,
      invoiceNum: form.invoiceNum, label: form.selectedPeriod || '',
      subtotalEx, gst, total, quantity,
    }),
  }).catch(() => {});
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmt(n) {
  if (n === undefined || n === null || isNaN(n)) return '$0.00';
  const abs = Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return n < 0 ? `-$${abs}` : `$${abs}`;
}

// A rate as saved (up to 4 dp, no trailing zeros) — the invoice itself still prints fmt().
function fmtRate(n) {
  const v = Number(n);
  return Number.isFinite(v) ? String(Math.round(v * 1e4) / 1e4) : '';
}

function fmtN(n) {
  return Number(n || 0).toLocaleString('en-AU');
}

function toDate(iso) {
  return new Date(iso + 'T00:00:00');
}

function addDays(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d + n);
  const yr = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const dy = String(date.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${dy}`;
}

function formatAU(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function formatMedium(iso) {
  if (!iso) return '';
  return toDate(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function dayName(iso) {
  if (!iso) return '';
  const d = toDate(iso).getDay();
  return WEEKDAYS[d === 0 ? 6 : d - 1];
}

function getDatesInRange(start, end) {
  if (!start || !end || start > end) return [];
  const dates = [];
  let cur = start;
  while (cur <= end) { dates.push(cur); cur = addDays(cur, 1); }
  return dates;
}

function isWeekend(iso) {
  const d = toDate(iso).getDay();
  return d === 0 || d === 6;
}

function weekLabel(weekIdx, periodStart) {
  if (!periodStart) return `Week ${weekIdx + 1}`;
  const start = addDays(periodStart, weekIdx * 7);
  const end   = addDays(start, 6);
  return `Week ${weekIdx + 1}  ${formatAU(start)}-${formatAU(end)}`;
}

// ── Default state builders ─────────────────────────────────────────────────────

// Rates are EX GST (option 1): line items net, GST added once at the bottom.
// No rate is held in a form: each tab reads the saved version in force for its
// period (Rates tab), so Reset, a new school row or a period change can never
// bring back an old figure. All schools share the one saved school rate.
function makeSchools() {
  return DEFAULT_SCHOOLS.map(name => ({ name, active: true, qty: 8 }));
}

function makeDoeForm() {
  const today = new Date().toISOString().slice(0, 10);
  const cp = findCurrentPeriod();
  return {
    invoiceNum: getNextInvoiceNum('doe'),
    invoiceDate: today,
    selectedPeriod: cp.label,
    periodStart: addDaysStatic(cp.startISO, 1),
    periodEnd: addDaysStatic(cp.endISO, -1),
    mainDesc: 'Service DET pickup delivery and lodgement cnr Best and Forbes St Woolloomooloo, Service DET pickup and delivery 1 collins St. delivery in morning from Newtown to Surry Hills, Observatory Hill Environmental Education Centre, Conservatorium pick ups',
    mainQty: 20,
    schools: makeSchools(),
    newSchool: '',
  };
}

function makeWaveForm() {
  const today = new Date().toISOString().slice(0, 10);
  const cp = findCurrentPeriod();
  return {
    invoiceNum: getNextInvoiceNum('wave'),
    invoiceDate: today,
    selectedPeriod: cp.label,
    periodStart: addDaysStatic(cp.startISO, 1),
    periodEnd: addDaysStatic(cp.endISO, -1),
    dailyItems: {},
  };
}

// 7 columns, Sunday→Saturday — column = the night's start day.
function makeNightWeek() {
  return {
    staff:    [0, 0, 0, 0, 0, 0, 0],
    leader:   [0, 0, 0, 0, 0, 0, 0],
    staffOT:  [0, 0, 0, 0, 0, 0, 0],
    leaderOT: [0, 0, 0, 0, 0, 0, 0],
    parcels:  [0, 0, 0, 0, 0, 0, 0],
  };
}

function makeNightForm() {
  const today = new Date().toISOString().slice(0, 10);
  const cp = findCurrentPeriod();
  return {
    invoiceNum: getNextInvoiceNum('night'),
    invoiceDate: today,
    selectedPeriod: cp.label,
    // The night grid weeks run Sunday→Saturday, so use the period's Sunday
    // start as-is (the other tabs shift to Monday; this one must not).
    periodStart: cp.startISO,
    weeks: [makeNightWeek(), makeNightWeek(), makeNightWeek(), makeNightWeek()],
  };
}

// ── Period selector component ──────────────────────────────────────────────────

function PeriodSelector({ form, setForm, autoFillDates }) {
  const handleSelect = (label) => {
    const period = PERIODS.find(p => p.label === label);
    if (!period) {
      setForm(f => ({ ...f, selectedPeriod: '' }));
      return;
    }
    setForm(f => autoFillDates(f, period));
  };

  return (
    <div className="form-group">
      <label className="form-label">Period</label>
      <select
        className="form-select"
        value={form.selectedPeriod}
        onChange={e => handleSelect(e.target.value)}
      >
        <option value="">— Select period —</option>
        {PERIODS.map(p => (
          <option key={p.label} value={p.label}>{p.label}</option>
        ))}
      </select>
    </div>
  );
}

// ── Invoice HTML generators ────────────────────────────────────────────────────
// Shared professional letterhead: black band with PH BOUVENG PTY LTD in gold,
// clean meta grid, dark table headers, gold-accented Total Due row, payment
// details footer. print-color-adjust keeps the band solid when printed.

const BASE_CSS = `
* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 11px; color: #1a1a1a; background: #fff; padding: 11mm 13mm; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
table { border-collapse: collapse; }
@page { size: A4 portrait; margin: 0; }
.lh { width: 100%; background: #141414; }
.lh td { padding: 15px 20px; vertical-align: middle; }
.lh .co { color: #C9A84C; font-size: 17px; font-weight: 700; letter-spacing: 2.5px; }
.lh .co-sub { color: #b5b5b5; font-size: 9px; margin-top: 5px; letter-spacing: 0.5px; line-height: 1.6; }
.lh .stamp { color: #fff; font-size: 20px; font-weight: 300; letter-spacing: 5px; text-align: right; white-space: nowrap; }
.lh .stamp-num { color: #C9A84C; font-size: 11px; font-weight: 700; letter-spacing: 1px; text-align: right; margin-top: 5px; }
.meta { width: 100%; border: 1px solid #e3e0d8; border-top: none; margin-bottom: 16px; }
.meta td { padding: 12px 20px; vertical-align: top; }
.blk-label { font-size: 8px; letter-spacing: 1.4px; color: #999; text-transform: uppercase; font-weight: 700; margin-bottom: 5px; }
.meta .to-name { font-size: 12.5px; font-weight: 700; }
.meta .to-line { font-size: 10.5px; color: #444; line-height: 1.6; }
.kv td { padding: 2px 0 2px 22px; font-size: 10.5px; white-space: nowrap; }
.kv .k { color: #888; }
.kv .v { font-weight: 600; text-align: right; }
.items-table { width: 100%; border: 1px solid #e3e0d8; margin-bottom: 0; }
.items-table th { background: #141414; color: #fff; padding: 8px 10px; text-align: left; font-size: 8.5px; text-transform: uppercase; letter-spacing: 1.1px; font-weight: 700; }
.items-table td { padding: 7px 10px; border-bottom: 1px solid #efece5; font-variant-numeric: tabular-nums; }
.trow-sub td { background: #f6f4ee; font-weight: 600; border-top: 2px solid #d8d2c2; }
.trow-gst td { background: #f6f4ee; color: #555; }
.trow-total td { background: #141414; color: #C9A84C; font-weight: 700; font-size: 12px; padding: 9px 10px; }
.payfoot { width: 100%; margin-top: 18px; }
.paybox { border: 1px solid #e3e0d8; border-left: 3px solid #C9A84C; background: #faf9f6; padding: 10px 14px; font-size: 10px; line-height: 1.8; width: 320px; }
.terms { font-size: 9px; color: #999; padding-top: 10px; line-height: 1.6; }
`;

// Shared letterhead + recipient/meta block.
function invoiceHead(recipient, invoiceNum, invoiceDate, periodLine) {
  return `
<table class="lh">
  <tr>
    <td>
      <div class="co">PH BOUVENG PTY LTD</div>
      <div class="co-sub">${COMPANY.poBox} &nbsp;·&nbsp; MOB ${COMPANY.mob} &nbsp;·&nbsp; ABN ${COMPANY.abn}</div>
    </td>
    <td>
      <div class="stamp">TAX INVOICE</div>
      <div class="stamp-num">No. ${invoiceNum || '—'}</div>
    </td>
  </tr>
</table>
<table class="meta">
  <tr>
    <td style="width:55%">
      <div class="blk-label">Invoice To</div>
      <div class="to-name">${recipient.company}</div>
      <div class="to-line">
        Attn: ${recipient.attn} — ${recipient.dept}<br>
        ${recipient.address}, ${recipient.suburb}
      </div>
    </td>
    <td>
      <table class="kv" style="float:right">
        <tr><td class="k">Invoice Date</td><td class="v">${invoiceDate}</td></tr>
        <tr><td class="k">Service</td><td class="v">${recipient.code}</td></tr>
        <tr><td class="k">Terms</td><td class="v">PVA — as per schedule</td></tr>
        ${periodLine ? `<tr><td class="k">Period</td><td class="v">${periodLine}</td></tr>` : ''}
      </table>
    </td>
  </tr>
</table>`;
}

// Shared payment-details footer.
function invoiceFoot(invoiceNum) {
  return `
<table class="payfoot">
  <tr>
    <td>
      <div class="paybox">
        <strong>Payment Details</strong><br>
        BSB: ${COMPANY.bsb} &nbsp;·&nbsp; ACC: ${COMPANY.acc}<br>
        Please quote invoice number <strong>${invoiceNum || ''}</strong> as your payment reference.
      </div>
      <div class="terms">Terms as per schedule · Hollypaul Engine · ABN ${COMPANY.abn}</div>
    </td>
  </tr>
</table>`;
}

function doeHTML(form) {
  const { invoiceNum, invoiceDate, periodStart, periodEnd, mainDesc, mainQty, mainRate, schools } = form;
  const dr = `${formatAU(periodStart)} - ${formatAU(periodEnd)}`;
  const mainTotal = mainQty * mainRate;
  let subtotal = mainTotal;

  const schoolRows = schools.filter(s => s.active).map((s, i) => {
    const t = s.qty * s.rate;
    subtotal += t;
    return `<tr style="background:${i % 2 === 0 ? '#faf9f6' : '#fff'}">
      <td>${s.name} ${dr}</td>
      <td style="text-align:center">${s.qty}</td>
      <td style="text-align:right">${fmt(s.rate)}</td>
      <td style="text-align:right">${fmt(t)}</td>
    </tr>`;
  }).join('');

  const gst = subtotal * 0.1;
  const total = subtotal + gst;

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${BASE_CSS}</style></head><body>
${invoiceHead(DOE_RECIPIENT, invoiceNum, invoiceDate, dr)}

<table class="items-table">
  <thead><tr>
    <th style="width:55%">Description</th>
    <th style="text-align:center">Qty</th>
    <th style="text-align:right">Unit Price (ex GST)</th>
    <th style="text-align:right">Total (ex GST)</th>
  </tr></thead>
  <tbody>
    <tr>
      <td>${mainDesc} ${dr}</td>
      <td style="text-align:center">${mainQty}</td>
      <td style="text-align:right">${fmt(mainRate)}</td>
      <td style="text-align:right">${fmt(mainTotal)}</td>
    </tr>
    ${schoolRows}
    <tr class="trow-sub"><td colspan="2"></td><td>Subtotal (ex GST)</td><td style="text-align:right">${fmt(subtotal)}</td></tr>
    <tr class="trow-gst"><td colspan="2"></td><td>GST 10.00%</td><td style="text-align:right">${fmt(gst)}</td></tr>
    <tr class="trow-total"><td colspan="2"></td>
      <td>Total Due (inc GST)</td>
      <td style="text-align:right">${fmt(total)}</td>
    </tr>
  </tbody>
</table>

${invoiceFoot(invoiceNum)}
</body></html>`;
}

function fmtInvDate(iso) {
  // "2026-04-20" → "20-Apr-2026"
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const [y, m, d] = iso.split('-').map(Number);
  return `${d}-${months[m-1]}-${y}`;
}

function waveHTML(form) {
  const { invoiceNum, invoiceDate, periodStart, periodEnd, unitPrice, adjPerItem, dailyItems } = form;
  const dates = getDatesInRange(periodStart, periodEnd);
  let totalItems = 0;
  let sumRows = 0;
  let firstDataRow = true;

  const rows = dates.map((iso, i) => {
    const weekend = isWeekend(iso);
    const rawVal = dailyItems[iso];
    // Blank string = non-delivery (public holiday or no run) → 0 cost, blank items cell
    const nonDelivery = weekend || rawVal === '' || rawVal === undefined;
    const items = nonDelivery ? 0 : (parseInt(rawVal) || 0);
    const cost = nonDelivery ? 0 : items * unitPrice;
    if (!nonDelivery) { totalItems += items; sumRows += cost; }

    const desc = firstDataRow ? '2nd Wave Express' : '';
    if (!weekend && rawVal !== '' && rawVal !== undefined) firstDataRow = false;

    const itemsCell = nonDelivery ? '' : (items > 0 ? fmtN(items) : '0');
    const bg = i % 2 === 0 ? '#f9f9f9' : '#fff';

    return `<tr style="background:${bg}">
      <td>${fmtInvDate(iso)}</td>
      <td>${desc}</td>
      <td>${dayName(iso)}</td>
      <td style="text-align:center">${itemsCell}</td>
      <td style="text-align:right">${fmt(unitPrice)}</td>
      <td style="text-align:right">${cost === 0 ? '$0.00' : fmt(cost)}</td>
    </tr>`;
  }).join('');

  const adjTotal = adjPerItem !== 0 ? totalItems * adjPerItem : 0;
  const subtotal = sumRows + adjTotal;
  const gst = subtotal * 0.1;
  const total = subtotal + gst;

  const adjRow = adjPerItem !== 0 ? `
    <tr>
      <td colspan="2"></td>
      <td style="font-style:italic;color:#555">Summary</td>
      <td style="text-align:center;font-weight:600">${fmtN(totalItems)}</td>
      <td style="text-align:right">${fmt(adjPerItem)}</td>
      <td style="text-align:right">${fmt(adjTotal)}</td>
    </tr>` : '';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${BASE_CSS}
body { font-size: 10px; }
.items-table td { padding: 4.5px 8px; }
</style></head><body>
${invoiceHead(WAVE_RECIPIENT, invoiceNum, invoiceDate, `${formatAU(periodStart)} - ${formatAU(periodEnd)}`)}

<table class="items-table">
  <thead>
    <tr>
      <th style="width:100px">Date</th>
      <th>Description</th>
      <th style="width:90px">Day</th>
      <th style="text-align:center;width:60px">Items</th>
      <th style="text-align:right;width:90px">Unit Price</th>
      <th style="text-align:right;width:90px">Cost</th>
    </tr>
  </thead>
  <tbody>
    ${rows}
  </tbody>
  <tfoot>
    ${adjRow}
    <tr class="trow-sub">
      <td colspan="3"></td>
      <td style="text-align:center">${fmtN(totalItems)}</td>
      <td style="text-align:right">Subtotal</td>
      <td style="text-align:right">${fmt(subtotal)}</td>
    </tr>
    <tr class="trow-gst">
      <td colspan="4"></td>
      <td style="text-align:right">GST 10.00%</td>
      <td style="text-align:right">${fmt(gst)}</td>
    </tr>
    <tr class="trow-total">
      <td colspan="4"></td>
      <td style="text-align:right">Total Due (inc GST)</td>
      <td style="text-align:right">${fmt(total)}</td>
    </tr>
  </tfoot>
</table>

${invoiceFoot(invoiceNum)}
</body></html>`;
}

function nightHTML(form) {
  const { invoiceNum, invoiceDate, periodStart, rates, weeks } = form;
  const DAY_COLS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  let grandTotal = 0;

  const weekTables = weeks.map((wk, wi) => {
    const staff    = nightSplit(wk.staff,    rates, 'staff');
    const leader   = nightSplit(wk.leader,   rates, 'leader');
    const staffOT  = nightSplit(wk.staffOT,  rates, 'staffOT');
    const leaderOT = nightSplit(wk.leaderOT, rates, 'leaderOT');
    const parcelsTot = wk.parcels.reduce((a, v) => a + parseInt(v || 0), 0);

    const weekTotal = staff.due + leader.due + staffOT.due + leaderOT.due;
    grandTotal += weekTotal;

    const totalHours = wk.staff.map((v, i) => parseFloat(v || 0) + parseFloat(wk.leader[i] || 0));

    const label = periodStart ? weekLabel(wi, periodStart) : `Week ${wi + 1}`;

    // Sunday hours (column 0) print as their own row at the Sunday rate so the
    // rate split stays auditable (row total × rate = row due, always); the
    // weekday row carries Mon–Sat only.
    const bodyRows = [];
    const pushRow = (rLabel, cells, tot, rateCell, dueCell) => bodyRows.push(`
    <tr style="background:${bodyRows.length % 2 === 0 ? '#faf9f6' : '#fff'}">
      <td style="padding:5px 8px">${rLabel}</td>
      ${cells.map(v => `<td style="padding:5px 4px;text-align:center">${v}</td>`).join('')}
      <td style="padding:5px 4px;text-align:center">${tot}</td>
      <td style="padding:5px 4px;text-align:right">${rateCell}</td>
      <td style="padding:5px 4px;text-align:right">${dueCell}</td>
    </tr>`);
    const cat = (rLabel, key, split) => {
      const vals = wk[key];
      if (split.sunH > 0) {
        pushRow(`${rLabel} — Sunday`, [vals[0] || 0, '', '', '', '', '', ''], split.sunH, fmt(split.sunRate), fmt(split.sunH * split.sunRate));
      }
      pushRow(rLabel, ['', ...vals.slice(1).map(v => v || 0)], split.wkH, fmt(split.wkRate), fmt(split.wkH * split.wkRate));
    };
    cat('Staff Hours', 'staff', staff);
    cat('Team leader', 'leader', leader);
    cat(`Staff O/T ${staffOT.sunH + staffOT.wkH > 0 ? '' : '1.5'}`, 'staffOT', staffOT);
    cat(`Leader O/T ${leaderOT.sunH + leaderOT.wkH > 0 ? '' : '1.5'}`, 'leaderOT', leaderOT);
    pushRow('Parcels', wk.parcels.map(v => v || 0), fmtN(parcelsTot), fmt(rates.parcels), '$0.00');

    return `
<table style="width:100%;border:1px solid #e3e0d8;margin-bottom:10px;border-collapse:collapse">
  <thead>
    <tr style="background:#141414;color:#fff">
      <th style="padding:6px 8px;text-align:left;width:110px">${label}</th>
      ${DAY_COLS.map(d => `<th style="padding:6px 3px;text-align:center">${d}</th>`).join('')}
      <th style="padding:6px 4px;text-align:center">Total</th>
      <th style="padding:6px 4px;text-align:right">Rate</th>
      <th style="padding:6px 4px;text-align:right">Total Due Ex GST</th>
    </tr>
  </thead>
  <tbody>
    ${bodyRows.join('')}
    <tr style="background:#f6f4ee;font-weight:600;border-top:2px solid #d8d2c2">
      <td style="padding:5px 8px">Total hours</td>
      ${totalHours.map(v => `<td style="padding:5px 4px;text-align:center">${v || 0}</td>`).join('')}
      <td style="padding:5px 4px;text-align:center">${Math.round(totalHours.reduce((a,v)=>a+v,0)*100)/100}</td>
      <td></td>
      <td style="padding:5px 4px;text-align:right">${fmt(weekTotal)}</td>
    </tr>
  </tbody>
</table>`;
  }).join('');

  const gst = grandTotal * 0.1;
  const total = grandTotal + gst;

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
${BASE_CSS}
body { font-size: 9px; }
</style></head><body>
${invoiceHead(NIGHT_RECIPIENT, invoiceNum, invoiceDate, '')}

${weekTables}

<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-top:4px">
  <div style="flex:1">
    <div class="paybox">
      <strong>Payment Details</strong><br>
      BSB: ${COMPANY.bsb} &nbsp;·&nbsp; ACC: ${COMPANY.acc}<br>
      Please quote invoice number <strong>${invoiceNum || ''}</strong> as your payment reference.
    </div>
    <div class="terms">Terms as per schedule · Hollypaul Engine · ABN ${COMPANY.abn}</div>
  </div>
  <table style="width:270px;border:1px solid #e3e0d8;font-size:10.5px;border-collapse:collapse;margin-left:16px">
    <tr>
      <td style="padding:6px 10px;background:#f6f4ee;font-weight:600">Subtotal (ex GST)</td>
      <td style="padding:6px 10px;background:#f6f4ee;text-align:right;font-weight:600">${fmt(grandTotal)}</td>
    </tr>
    <tr>
      <td style="padding:6px 10px;background:#f6f4ee;color:#555">GST 10.00%</td>
      <td style="padding:6px 10px;background:#f6f4ee;text-align:right;color:#555">${fmt(gst)}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px;background:#141414;color:#C9A84C;font-weight:700;font-size:11.5px">Total Due (inc GST)</td>
      <td style="padding:8px 10px;background:#141414;color:#C9A84C;font-weight:700;font-size:11.5px;text-align:right">${fmt(total)}</td>
    </tr>
  </table>
</div>
</body></html>`;
}

// ── Print helper ───────────────────────────────────────────────────────────────

function printInvoice(html) {
  const win = window.open('', '_blank');
  if (!win) { alert('Please allow pop-ups to print the invoice.'); return; }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 400);
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

function NightWeekTable({ weekIdx, week, periodStart, rates, onChange }) {
  // Columns run Sunday→Saturday: each night sits under its START day, exactly
  // matching Night Sorters → Actual times. Sunday bills at the Sunday rates.
  const DAY_COLS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const ROWS = [
    { key: 'staff',    label: 'Staff Hours',  step: '0.25' },
    { key: 'leader',   label: 'Team Leader',  step: '0.25' },
    { key: 'staffOT',  label: 'Staff O/T',    step: '0.25' },
    { key: 'leaderOT', label: 'Leader O/T',   step: '0.25' },
    { key: 'parcels',  label: 'Parcels',      step: '1' },
  ];
  const sunBg = 'rgba(201,168,76,0.10)';

  const label = periodStart ? weekLabel(weekIdx, periodStart) : `Week ${weekIdx + 1}`;

  const weekTotal = nightSplit(week.staff, rates, 'staff').due +
                    nightSplit(week.leader, rates, 'leader').due +
                    nightSplit(week.staffOT, rates, 'staffOT').due +
                    nightSplit(week.leaderOT, rates, 'leaderOT').due;

  return (
    <div className="card" style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}>
      <div style={{ background: '#1a3a6b', color: '#fff', padding: '8px 14px', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{label}</span>
        <span style={{ fontSize: '0.9rem' }}>Week Total Ex GST: <strong>{fmt(weekTotal)}</strong></span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ background: 'rgba(0,0,0,0.05)' }}>
              <th style={{ padding: '6px 10px', textAlign: 'left', width: 110 }}>Row</th>
              {DAY_COLS.map((d, di) => (
                <th key={d} style={{ padding: '6px 6px', textAlign: 'center', width: 76, background: di === 0 ? sunBg : undefined }}
                  title={di === 0 ? 'Sunday-start night — bills at the Sunday rates' : undefined}>
                  {d}{di === 0 ? ' ★' : ''}
                </th>
              ))}
              <th style={{ padding: '6px 8px', textAlign: 'center' }}>Total</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Rate</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Due (ex GST)</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map(({ key, label: rLabel, step }, ri) => {
              const vals = week[key];
              const isParcels = key === 'parcels';
              const split = isParcels ? null : nightSplit(vals, rates, key);
              const tot = Math.round(vals.reduce((a, v) => a + parseFloat(v || 0), 0) * 100) / 100;
              const due = isParcels ? 0 : split.due;
              return (
                <tr key={key} style={{ borderTop: '1px solid rgba(128,128,128,0.15)', background: ri % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)' }}>
                  <td style={{ padding: '4px 10px', fontSize: '0.8rem', color: 'var(--hp-muted)' }}>{rLabel}</td>
                  {vals.map((v, di) => (
                    <td key={di} style={{ padding: '2px 3px', textAlign: 'center', background: di === 0 ? sunBg : undefined }}>
                      <input
                        type="number" min="0" step={step}
                        value={v}
                        onChange={e => onChange(weekIdx, key, di, e.target.value)}
                        style={{ width: 64, textAlign: 'center', background: 'transparent', border: '1px solid rgba(128,128,128,0.3)', borderRadius: 4, padding: '3px 4px', color: 'inherit', fontSize: '0.82rem' }}
                      />
                    </td>
                  ))}
                  <td style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 600 }}>{tot}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--hp-muted)', whiteSpace: 'nowrap' }}>
                    {isParcels ? fmt(rates.parcels) : (
                      <>
                        {fmt(split.wkRate)}
                        {split.sunRate !== split.wkRate && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--hp-gold)' }}>Sun {fmt(split.sunRate)}</div>
                        )}
                      </>
                    )}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmt(due)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── DoE Tab ────────────────────────────────────────────────────────────────────

function DoeTab({ form: formState, setForm, rates, rateNote }) {
  // Saved rates for this period, laid over the form so the screen and the PDF agree.
  const form = {
    ...formState,
    mainRate: rates.doe.mainRate,
    schools: formState.schools.map(s => ({ ...s, rate: rates.doe.schoolRate })),
  };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleSchool = i => setForm(f => {
    const schools = [...f.schools];
    schools[i] = { ...schools[i], active: !schools[i].active };
    return { ...f, schools };
  });

  const updateSchool = (i, key, val) => setForm(f => {
    const schools = [...f.schools];
    schools[i] = { ...schools[i], [key]: val };
    return { ...f, schools };
  });

  const addSchool = () => {
    if (!form.newSchool.trim()) return;
    setForm(f => ({
      ...f,
      schools: [...f.schools, { name: f.newSchool.trim(), active: true, qty: 8 }],
      newSchool: '',
    }));
  };

  const removeSchool = i => setForm(f => ({ ...f, schools: f.schools.filter((_, idx) => idx !== i) }));

  const activeSchools = form.schools.filter(s => s.active);
  const schoolsTotal = activeSchools.reduce((a, s) => a + s.qty * s.rate, 0);
  const mainTotal = form.mainQty * form.mainRate;
  const subtotal = mainTotal + schoolsTotal;
  const gst = subtotal * 0.1;
  const total = subtotal + gst;

  const handlePrint = () => {
    saveInvoiceNum('doe', form.invoiceNum);
    saveTaxInvoiceTotal('doe', form, { subtotalEx: subtotal, gst, total, quantity: form.mainQty });
    printInvoice(doeHTML(form));
  };

  return (
    <div>
      {rateNote}
      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <Field label="Invoice Number">
          <input className="form-input" value={form.invoiceNum} onChange={e => set('invoiceNum', e.target.value)} placeholder="e.g. 579" />
        </Field>
        <Field label="Invoice Date">
          <input className="form-input" type="text" value={form.invoiceDate} onChange={e => set('invoiceDate', e.target.value)} placeholder="e.g. 15/05/2026" />
        </Field>
        <PeriodSelector form={form} setForm={setForm} autoFillDates={(f, p) => ({
          ...f,
          selectedPeriod: p.label,
          periodStart: addDays(p.startISO, 1),
          periodEnd: addDays(p.endISO, -1),
        })} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 10, color: 'var(--hp-gold)' }}>Main Service Line</div>
        <Field label="Description">
          <textarea className="form-textarea" rows={3} value={form.mainDesc} onChange={e => set('mainDesc', e.target.value)} />
        </Field>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 8 }}>
          <Field label="Qty (days)">
            <input className="form-input" type="number" min="0" value={form.mainQty} onChange={e => set('mainQty', parseFloat(e.target.value) || 0)} />
          </Field>
          <Field label="Unit Price (ex GST) — Rates tab">
            <input className="form-input" readOnly value={fmtRate(form.mainRate)} title="Change on the Rates tab" style={{ background: 'transparent' }} />
          </Field>
          <Field label="Total (ex GST)">
            <input className="form-input" readOnly value={fmt(mainTotal)} style={{ background: 'transparent', color: 'var(--hp-gold)' }} />
          </Field>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 10, color: 'var(--hp-gold)' }}>Schools / Locations</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(128,128,128,0.2)' }}>
              <th style={{ padding: '5px 8px', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '5px 8px', textAlign: 'center', width: 70 }}>Qty</th>
              <th style={{ padding: '5px 8px', textAlign: 'right', width: 90 }}>Rate (ex GST)</th>
              <th style={{ padding: '5px 8px', textAlign: 'right', width: 90 }}>Total (ex GST)</th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {form.schools.map((s, i) => (
              <tr key={i} style={{ borderBottom: '1px solid rgba(128,128,128,0.1)', opacity: s.active ? 1 : 0.4 }}>
                <td style={{ padding: '4px 8px' }}>
                  <input style={{ background: 'transparent', border: 'none', width: '100%', color: 'inherit', fontSize: '0.85rem' }}
                    value={s.name} onChange={e => updateSchool(i, 'name', e.target.value)} />
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                  <input type="number" min="0" style={{ width: 60, textAlign: 'center', background: 'transparent', border: '1px solid rgba(128,128,128,0.3)', borderRadius: 4, padding: '2px 4px', color: 'inherit', fontSize: '0.85rem' }}
                    value={s.qty} onChange={e => updateSchool(i, 'qty', parseInt(e.target.value) || 0)} />
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'right' }} title="All schools share the saved school rate (Rates tab)">
                  {fmtRate(s.rate)}
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--hp-muted)' }}>{fmt(s.qty * s.rate)}</td>
                <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                  <button onClick={() => toggleSchool(i)} className="btn btn-sm btn-ghost" title={s.active ? 'Disable' : 'Enable'} style={{ padding: '2px 6px', fontSize: '0.75rem' }}>
                    {s.active ? '✓' : '○'}
                  </button>
                  <button onClick={() => removeSchool(i)} className="btn btn-sm btn-ghost" style={{ padding: '2px 6px', color: 'var(--hp-danger)' }}>
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <input className="form-input" placeholder="Add school / location name..." value={form.newSchool}
            onChange={e => setForm(f => ({ ...f, newSchool: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && addSchool()}
            style={{ flex: 1 }} />
          <button className="btn btn-secondary btn-sm" onClick={addSchool}><Plus size={14} /></button>
        </div>
      </div>

      <div className="card card-gold" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '1.05rem' }}>
          <span style={{ color: 'var(--hp-muted)', marginRight: 16 }}>Subtotal (ex GST): {fmt(subtotal)}</span>
          <span style={{ color: 'var(--hp-muted)', marginRight: 16 }}>GST: {fmt(gst)}</span>
          <strong style={{ color: 'var(--hp-gold)', fontSize: '1.15rem' }}>Total Due (inc GST): {fmt(total)}</strong>
        </div>
        <button className="btn btn-primary" onClick={handlePrint} disabled={!form.invoiceNum}
          title={form.invoiceNum ? '' : 'Invoice number loading — or enter one manually'}
          style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: form.invoiceNum ? 1 : 0.5 }}>
          <Printer size={16} /> Print / Save PDF
        </button>
      </div>
    </div>
  );
}

// ── 2nd Wave Tab ───────────────────────────────────────────────────────────────

async function fetchWaveItemsFromSheet(periodEndISO) {
  // Route through the Netlify proxy → wave-sb → the `parcel_events` table (the
  // authoritative express source post-migration). The raw Apps Script endpoint
  // still scans the FROZEN Sheet1 tab (only a best-effort import mirror), which
  // drifts from parcel_events — that was under-reading EP on the tax invoice.
  const res = await fetch('/.netlify/functions/appsscript', {
    method: 'POST',
    body: JSON.stringify({ action: 'getWaveItems', payPeriod: periodEndISO }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return { items: data.items || [], flagged: data.flagged || [] };
}

function WaveTab({ form: formState, setForm, rates, rateNote }) {
  const form = { ...formState, unitPrice: rates.wave.unitPrice, adjPerItem: rates.wave.adjPerItem };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [sheetStatus, setSheetStatus] = useState('');
  const [sheetLoading, setSheetLoading] = useState(false);
  const [loadedDates, setLoadedDates] = useState(new Set());
  const [flaggedRows, setFlaggedRows] = useState([]);
  // decisions: Map of index → 'accepted' | 'rejected' (null = pending, not yet in totals)
  const [decisions, setDecisions] = useState({});

  const setItem = (iso, val) => setForm(f => ({
    ...f,
    dailyItems: { ...f.dailyItems, [iso]: val },
  }));

  // Mark a weekday as non-delivery (blank = no cost)
  const clearDay = (iso) => setForm(f => {
    const next = { ...f.dailyItems };
    delete next[iso];
    return { ...f, dailyItems: next };
  });

  const handleLoadFromSheet = async () => {
    if (!form.periodEnd) { setSheetStatus('Select a period first.'); return; }
    setSheetLoading(true);
    setSheetStatus('Loading from Google Sheet…');
    try {
      // Sheet1 Pay Period column uses the SATURDAY end date (RCTI anchor).
      // form.periodEnd is Friday (last working day), so add 1 day to get Saturday.
      const payPeriodSat = addDays(form.periodEnd, 1);
      const { items, flagged } = await fetchWaveItemsFromSheet(payPeriodSat);
      if (items.length === 0 && flagged.length === 0) { setSheetStatus('No data found for this period in Sheet1.'); setSheetLoading(false); return; }
      // Clear all dates in this period first, then apply loaded data
      const cleared = {};
      let cur = form.periodStart;
      while (cur <= form.periodEnd) { cleared[cur] = ''; cur = addDays(cur, 1); }
      const newItems = { ...cleared };
      const loaded = new Set();
      items.forEach(({ date, total }) => {
        const n = parseInt(total) || 0;
        newItems[date] = String(n); // load actual count; adjust to 40 minimum manually before printing
        loaded.add(date);
      });
      setForm(f => ({ ...f, dailyItems: { ...f.dailyItems, ...newItems } }));
      setLoadedDates(loaded);
      setFlaggedRows(flagged);
      setDecisions({});
      const daysWithItems = items.filter(x => parseInt(x.total) > 0).length;
      const flagMsg = flagged.length > 0 ? ` — ⚠️ ${flagged.length} unusual signoff${flagged.length > 1 ? 's' : ''} flagged for review below.` : '';
      setSheetStatus(`Loaded ${daysWithItems} delivery days. All cells editable — adjust before printing.${flagMsg}`);
    } catch (err) {
      setSheetStatus(`Error: ${err.message}`);
    }
    setSheetLoading(false);
  };

  const dates = getDatesInRange(form.periodStart, form.periodEnd);

  // Calculate totals: blank = non-delivery (no cost), '0' = zero delivery (no cost), number = billable
  let totalItems = 0;
  let sumRows = 0;
  dates.forEach(iso => {
    const val = form.dailyItems[iso];
    if (!isWeekend(iso) && val !== undefined && val !== '') {
      const n = parseInt(val) || 0;
      totalItems += n;
      sumRows += n * form.unitPrice;
    }
  });

  const adjTotal = form.adjPerItem !== 0 ? totalItems * form.adjPerItem : 0;
  const subtotal = sumRows + adjTotal;
  const gst = subtotal * 0.1;
  const total = subtotal + gst;

  const handlePrint = () => {
    saveInvoiceNum('wave', form.invoiceNum);
    saveTaxInvoiceTotal('wave', form, { subtotalEx: subtotal, gst, total, quantity: totalItems });
    printInvoice(waveHTML(form));
  };

  return (
    <div>
      {rateNote}
      {/* Header row */}
      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <Field label="Invoice Number">
          <input className="form-input" value={form.invoiceNum} onChange={e => set('invoiceNum', e.target.value)} placeholder="e.g. 584" />
        </Field>
        <Field label="Invoice Date">
          <input className="form-input" type="text" value={form.invoiceDate} onChange={e => set('invoiceDate', e.target.value)} placeholder="e.g. 15 May 2026" />
        </Field>
        <PeriodSelector form={form} setForm={setForm} autoFillDates={(f, p) => ({
          ...f,
          selectedPeriod: p.label,
          periodStart: addDays(p.startISO, 1),
          periodEnd: addDays(p.endISO, -1),
          dailyItems: {},
        })} />
        <Field label="Unit Price ($) — Rates tab">
          <input className="form-input" readOnly value={fmtRate(form.unitPrice)} title="Change on the Rates tab" style={{ background: 'transparent' }} />
        </Field>
      </div>

      {/* Daily items table */}
      <div className="card" style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
        {/* Table header bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'rgba(0,0,0,0.05)', borderBottom: '1px solid rgba(128,128,128,0.15)' }}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Daily Express Post Items</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {sheetStatus && (
              <span style={{ fontSize: '0.78rem', color: sheetStatus.startsWith('Error') ? 'var(--hp-danger)' : 'var(--hp-muted)', maxWidth: 320 }}>
                {sheetStatus}
              </span>
            )}
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleLoadFromSheet}
              disabled={sheetLoading || !form.periodEnd}
              style={{ display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}
            >
              <Download size={13} /> {sheetLoading ? 'Loading…' : 'Load from Sheet1'}
            </button>
          </div>
        </div>

        {/* Flagged signoff review panel */}
        {flaggedRows.length > 0 && (
          <div style={{ margin: '0 0 16px', border: '1.5px solid #f59e0b', borderRadius: 8, background: '#fffbeb', padding: '12px 16px' }}>
            <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 8, fontSize: '0.85rem' }}>
              ⚠️ Unusual signoffs found — review before including in totals
            </div>
            <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: '#78350f', fontWeight: 600 }}>
                  <th style={{ textAlign: 'left', padding: '3px 8px' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '3px 8px' }}>Driver</th>
                  <th style={{ textAlign: 'left', padding: '3px 8px' }}>Signoff ID</th>
                  <th style={{ textAlign: 'center', padding: '3px 8px' }}>Items</th>
                  <th style={{ textAlign: 'center', padding: '3px 8px' }}>Decision</th>
                </tr>
              </thead>
              <tbody>
                {flaggedRows.map((row, i) => {
                  const decision = decisions[i]; // 'accepted' | 'rejected' | undefined (pending)
                  return (
                    <tr key={i} style={{ opacity: decision === 'rejected' ? 0.45 : 1, borderTop: '1px solid #fde68a' }}>
                      <td style={{ padding: '4px 8px' }}>{row.date}</td>
                      <td style={{ padding: '4px 8px' }}>{row.driver}</td>
                      <td style={{ padding: '4px 8px', fontFamily: 'monospace', color: '#b45309' }}>{row.signoff}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'center' }}>{row.total}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'center', display: 'flex', gap: 6, justifyContent: 'center' }}>
                        <button
                          style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #16a34a', background: decision === 'accepted' ? '#16a34a' : '#fff', color: decision === 'accepted' ? '#fff' : '#15803d', cursor: decision === 'accepted' ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.78rem' }}
                          onClick={() => {
                            if (decision === 'accepted') return;
                            setDecisions(d => ({ ...d, [i]: 'accepted' }));
                            // Add to daily total (only if not previously accepted)
                            setForm(f => {
                              const cur = parseInt(f.dailyItems[row.date]) || 0;
                              return { ...f, dailyItems: { ...f.dailyItems, [row.date]: String(cur + row.total) } };
                            });
                          }}
                        >✓ Accept</button>
                        <button
                          style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #dc2626', background: decision === 'rejected' ? '#dc2626' : '#fff', color: decision === 'rejected' ? '#fff' : '#dc2626', cursor: decision === 'rejected' ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.78rem' }}
                          onClick={() => {
                            if (decision === 'rejected') return;
                            // If was previously accepted, remove from total first
                            if (decision === 'accepted') {
                              setForm(f => {
                                const cur = parseInt(f.dailyItems[row.date]) || 0;
                                return { ...f, dailyItems: { ...f.dailyItems, [row.date]: String(Math.max(0, cur - row.total)) } };
                              });
                            }
                            setDecisions(d => ({ ...d, [i]: 'rejected' }));
                          }}
                        >✗ Reject</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {dates.length === 0 && (
          <div style={{ color: 'var(--hp-muted)', textAlign: 'center', padding: '28px 0', fontSize: '0.9rem' }}>
            Select a period above to begin
          </div>
        )}

        {dates.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.04)', borderBottom: '2px solid rgba(128,128,128,0.2)' }}>
                <th style={{ padding: '7px 12px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                <th style={{ padding: '7px 8px', textAlign: 'left', fontWeight: 600 }}>Day</th>
                <th style={{ padding: '7px 8px', textAlign: 'center', fontWeight: 600 }}>Items</th>
                <th style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 600 }}>Unit Price</th>
                <th style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600 }}>Cost (ex GST)</th>
                <th style={{ width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {dates.map((iso, i) => {
                const weekend = isWeekend(iso);
                const val = form.dailyItems[iso];
                const nonDelivery = weekend || val === undefined || val === '';
                const items = nonDelivery ? 0 : (parseInt(val) || 0);
                const cost = nonDelivery ? 0 : items * form.unitPrice;
                const fromSheet = loadedDates.has(iso);

                const rowStyle = {
                  borderBottom: '1px solid rgba(128,128,128,0.08)',
                  background: weekend
                    ? 'rgba(0,0,0,0.03)'
                    : i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.015)',
                  opacity: weekend ? 0.5 : 1,
                };

                return (
                  <tr key={iso} style={rowStyle}>
                    <td style={{ padding: '5px 12px', fontFamily: 'monospace', fontSize: '0.82rem' }}>
                      {fmtInvDate(iso)}
                    </td>
                    <td style={{ padding: '5px 8px', color: 'var(--hp-muted)', fontSize: '0.82rem' }}>
                      {dayName(iso)}
                    </td>
                    <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                      {!weekend && (
                        <input
                          type="number" min="0" step="1"
                          value={val !== undefined ? val : ''}
                          onChange={e => setItem(iso, e.target.value)}
                          placeholder="—"
                          title={fromSheet ? 'Loaded from Sheet1 — editable' : 'Enter item count'}
                          style={{
                            width: 72, textAlign: 'center',
                            background: fromSheet && val !== undefined && val !== '' ? 'rgba(26,90,60,0.12)' : 'transparent',
                            border: `1px solid ${fromSheet && val !== undefined ? 'rgba(26,120,80,0.4)' : 'rgba(128,128,128,0.3)'}`,
                            borderRadius: 4, padding: '3px 5px', color: 'inherit', fontSize: '0.84rem',
                          }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', color: 'var(--hp-muted)', fontSize: '0.82rem' }}>
                      {fmt(form.unitPrice)}
                    </td>
                    <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: cost > 0 ? 600 : 'normal', color: cost > 0 ? 'inherit' : 'var(--hp-muted)' }}>
                      {cost > 0 ? fmt(cost) : weekend ? '—' : val === '' || val === undefined ? <span style={{ fontSize: '0.75rem', color: 'var(--hp-muted)' }}>Non-delivery</span> : '$0.00'}
                    </td>
                    <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                      {!weekend && (val !== undefined && val !== '') && (
                        <button
                          onClick={() => clearDay(iso)}
                          title="Mark as non-delivery day"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-muted)', padding: '1px 4px', fontSize: '0.75rem', opacity: 0.6 }}
                        >✕</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Summary adjustment */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 10, color: 'var(--hp-gold)', fontSize: '0.9rem' }}>Summary Adjustment (Rate Correction)</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 16, alignItems: 'end' }}>
          <Field label="Adj. per item — Rates tab (negative = reduction, 0 = none)">
            <input className="form-input" readOnly value={fmtRate(form.adjPerItem)} title="Change on the Rates tab"
              style={{ width: 140, background: 'transparent' }} />
          </Field>
          {totalItems > 0 && form.adjPerItem !== 0 && (
            <div style={{ paddingBottom: 4, fontSize: '0.85rem', color: 'var(--hp-muted)' }}>
              {fmtN(totalItems)} items × {fmt(form.adjPerItem)} = <strong>{fmt(adjTotal)}</strong>
            </div>
          )}
        </div>
      </div>

      {/* Totals bar + print */}
      <div className="card card-gold" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '0.95rem', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <span><span style={{ color: 'var(--hp-muted)' }}>Items: </span><strong>{fmtN(totalItems)}</strong></span>
          <span><span style={{ color: 'var(--hp-muted)' }}>Subtotal: </span><strong>{fmt(subtotal)}</strong></span>
          <span><span style={{ color: 'var(--hp-muted)' }}>GST: </span><strong>{fmt(gst)}</strong></span>
          <span style={{ fontSize: '1.05rem' }}><span style={{ color: 'var(--hp-muted)' }}>Total Due: </span><strong style={{ color: 'var(--hp-gold)' }}>{fmt(total)}</strong></span>
        </div>
        <button className="btn btn-primary" onClick={handlePrint} disabled={!form.invoiceNum || !form.periodStart}
          style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
          <Printer size={16} /> Print / Save PDF
        </button>
      </div>
    </div>
  );
}

// ── Deputy CSV parser ──────────────────────────────────────────────────────────

function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (const ch of line) {
    if (ch === '"') { inQuotes = !inQuotes; continue; }
    if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ''; continue; }
    current += ch;
  }
  result.push(current.trim());
  return result;
}

function parseDeputyCSV(csvText) {
  const lines = csvText.trim().split(/\r?\n/);
  if (lines.length < 2) return null;
  const headers = parseCSVLine(lines[0]);
  const col = name => headers.indexOf(name);
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const vals = parseCSVLine(lines[i]);
    if (vals.length < 5) continue;
    const date  = vals[col('Date')]       || '';
    const area  = vals[col('Area Name')]  || '';
    const hours = parseFloat(vals[col('Total Hours')]) || 0;
    if (!date || !hours) continue;
    rows.push({ date, area, hours });
  }
  return rows;
}

function buildWeeksFromRows(rows, periodStart) {
  const weeks = [makeNightWeek(), makeNightWeek(), makeNightWeek(), makeNightWeek()];
  const [py, pm, pd] = periodStart.split('-').map(Number);
  const pTime = new Date(py, pm - 1, pd).getTime();

  rows.forEach(({ date, area, hours }) => {
    const [y, m, d] = date.split('-').map(Number);
    const dTime = new Date(y, m - 1, d).getTime();
    const dayDiff = Math.round((dTime - pTime) / 86400000);
    if (dayDiff < 0 || dayDiff >= 28) return;

    const weekIdx = Math.floor(dayDiff / 7);
    const dow     = new Date(y, m - 1, d).getDay(); // 0=Sun … 6=Sat
    // Deputy rows are dated by the night's START day; columns run Sun→Sat,
    // so the day-of-week maps straight onto the column (Sunday = column 0).
    const colIdx  = dow;

    const isLeader = area.trim() === 'Team Leader';
    const key = isLeader ? 'leader' : 'staff';
    weeks[weekIdx][key][colIdx] = Math.round((parseFloat(weeks[weekIdx][key][colIdx] || 0) + hours) * 100) / 100;
  });

  return weeks;
}

// ── Actual-times import (Night Sorters app data) ──────────────────────────────
// Fills the 4-week PVA grid from the same data as the Night Sorters → Actual
// times ledger: per night, hours = start → AP finish; overtime = ONLY hours
// beyond 8 on the shift (workplace-law basis, operator directive 2026-07-03 —
// running past the scheduled finish is NOT overtime under 8h). The O/T rows
// carry only those extra hours, billed at half the base rate on top, i.e.
// 1.5× all-in. Each night lands under its
// START day (Sun→Sat columns), aligned 1:1 with the ledger; the Sunday night
// bills at the Sunday rates. The Team Leader shift is
// isolated into the leader rows: the roster's "Team Leader" / "Team Leader /
// Runner" slot when a roster was published, otherwise 1 per night (the standing
// pattern); every other shift bills as staff.
const nsApi = (action, payload) => fetch('/.netlify/functions/appsscript', {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }),
}).then(r => r.json());

async function buildWeeksFromActualTimes(period) {
  const BREAK_H = 0.5;   // 30-min unpaid break — removed from every shift, never billed to AP
  const from = period.startISO, to = period.endISO;
  // Two data sources, break removed from BOTH:
  //  1. Per-sorter APPROVED timesheets (Night Sorters → Approvals). Their Hrs
  //     column is already actual span − actual break (worked_hours) — the exact,
  //     preferred figure. Only exists for nights sorters clocked in / were seeded.
  //  2. Finish-times ledger (Actual times) — one finish + shift count per night,
  //     backfilled for the whole period. Used as a FALLBACK for any night with no
  //     per-sorter timesheets, minus a flat 30-min break per shift.
  const [t, s, r, f] = await Promise.all([
    nsApi('getSorterTimesheetsAdmin', { status: 'All', from, to, limit: 2000 }),
    nsApi('getSorterSlots', {}),
    nsApi('getSorterRoster', { from, to }),
    nsApi('nsListFinishTimes', { from, to }),
  ]);
  if (!t || !t.success) throw new Error((t && t.error) || 'Could not load the timesheet approvals');

  const weeks = [makeNightWeek(), makeNightWeek(), makeNightWeek(), makeNightWeek()];
  const [py, pm, pd] = from.split('-').map(Number);
  const pTime = new Date(py, pm - 1, pd).getTime();
  // The column mirrors each night's day-of-week 1:1, so the TI matches the
  // approvals/ledger day for day. Until 31 Jul 2026 nights are dated by the
  // morning the shift ends (operator directive), so the Sunday column stays
  // empty; from 1 Aug the keying switches to the night's start day and Sunday
  // nights land in the Sunday column at the Sunday rates.
  const cw = (date) => {
    const [y, m, d] = (date || '').split('-').map(Number);
    if (!y) return null;
    const dayDiff = Math.round((new Date(y, m - 1, d).getTime() - pTime) / 86400000);
    if (dayDiff < 0 || dayDiff >= 28) return null;
    return { wi: Math.floor(dayDiff / 7), col: new Date(y, m - 1, d).getDay() };
  };
  const add = (wi, key, col, v) => { weeks[wi][key][col] = Math.round(((parseFloat(weeks[wi][key][col]) || 0) + v) * 100) / 100; };
  const t2m = (x) => { const m = /^(\d{1,2}):(\d{2})/.exec((x || '').trim()); return m ? (+m[1]) * 60 + (+m[2]) : null; };

  // ── Ledger index: per night → start / finish / shift count / TL count. This
  //    is the operator-maintained record of which nights ran and how many
  //    shifts, so it also tells us the EXPECTED headcount for a night. ──
  const slotInfo = {};
  ((s && s.slots) || []).forEach(sl => { slotInfo[sl.Slot] = { cat: sl.Category || '', isTL: /team leader/i.test(sl.Slot || '') }; });
  const byDate = {};
  ((r && r.roster) || []).forEach(rw => {
    if (!(rw.Login || '').trim()) return;
    const info = slotInfo[rw.Slot] || { cat: '', isTL: false };
    const e = byDate[rw.Date] || (byDate[rw.Date] = { shifts: 0, tl: 0, ranges: {} });
    e.shifts++; if (info.isTL) e.tl++;
    // Runners are always +1h, so only sorter shifts vote for the night's times.
    if (info.cat === 'sorter' && rw.Start && rw.End) { const k = rw.Start + '–' + rw.End; e.ranges[k] = (e.ranges[k] || 0) + 1; }
  });
  const ledger = {};
  ((f && f.finishes) || []).forEach(fin => {
    const date = fin.shiftDate;
    const e = byDate[date];
    let best = '';
    if (e) Object.entries(e.ranges).forEach(([k, n]) => { if (!best || n > e.ranges[best]) best = k; });
    const [rStart] = best ? best.split('–') : [''];
    const shifts = fin.shifts != null ? fin.shifts : (e ? e.shifts : 0);
    ledger[date] = {
      start: fin.startTime || rStart,                          // manual override beats roster
      finish: fin.finishTime,
      shifts,
      tl: Math.min(e && e.tl ? e.tl : 1, shifts || 1),
    };
  });

  // ── Approved per-sorter timesheets, grouped by night. Zero/blank-hour rows
  //    (e.g. TEST/ZZTEST accounts) are ignored so they can't stand in for a
  //    real night's coverage. ──
  let pending = 0;
  const tsByDate = {};
  ((t.timesheets) || []).forEach(row => {
    if ((row.Status || '') === 'Discarded' || (row.Status || '') === 'Absent') return;
    if (!cw(row.Date)) return;                                 // outside this period
    if ((row.Status || '') !== 'Approved') { pending++; return; }  // never bill unapproved
    const hours = parseFloat(row.workedHours);                // = span − actual break
    if (!(hours > 0)) return;
    (tsByDate[row.Date] || (tsByDate[row.Date] = [])).push({ hours, slot: (row.slot || ''), who: `${row.Name || row.Login} ${row.Date}` });
  });

  // ── Bill each night from ONE source. A night uses its per-sorter timesheets
  //    only when they COVER the whole night (count ≥ the ledger's shift count,
  //    or the ledger has no entry) — otherwise partial clock-ins would silently
  //    under-bill, so it falls back to the ledger minus a flat 30-min break. ──
  let filled = 0; const skipped = []; const otNights = []; const unrostered = [];
  const tsNights = []; const fallbackNights = []; const partialNights = [];
  const allDates = new Set([...Object.keys(ledger), ...Object.keys(tsByDate)]);
  allDates.forEach(date => {
    const w = cw(date);
    if (!w) return;
    const led = ledger[date];
    const ts = tsByDate[date] || [];
    const ledShifts = led ? (led.shifts || 0) : 0;
    const covered = ts.length > 0 && (ledShifts === 0 || ts.length >= ledShifts);

    if (covered) {                                             // full per-sorter coverage → exact break
      ts.forEach(x => {
        const isLeader = /team leader/i.test(x.slot);          // leader rate for TL only; runners = staff
        if (!x.slot.trim()) unrostered.push(x.who);
        const otH = Math.max(0, x.hours - 8);                  // genuine O/T only (worked hours >8)
        if (otH > 0) otNights.push(date);
        add(w.wi, isLeader ? 'leader' : 'staff', w.col, x.hours);
        if (otH > 0) add(w.wi, isLeader ? 'leaderOT' : 'staffOT', w.col, otH);
      });
      tsNights.push(date); filled++;
      return;
    }

    if (ts.length > 0) partialNights.push(`${date} (${ts.length}/${ledShifts})`);  // clock-ins superseded by the ledger
    if (!led) return;
    const s0 = t2m(led.start), a0 = t2m(led.finish);
    if (s0 == null || a0 == null || !ledShifts) { if (led.finish || ledShifts) skipped.push(date); return; }
    const spanH = (((a0 - s0) + 1440) % 1440) / 60;
    const workedH = Math.max(0, spanH - BREAK_H);             // remove the 30-min break
    const otH = Math.max(0, workedH - 8);
    if (otH > 0) otNights.push(date);
    const staffN = ledShifts - led.tl;
    add(w.wi, 'staff', w.col, workedH * staffN);
    add(w.wi, 'leader', w.col, workedH * led.tl);
    if (otH > 0) { add(w.wi, 'staffOT', w.col, otH * staffN); add(w.wi, 'leaderOT', w.col, otH * led.tl); }
    fallbackNights.push(date); filled++;
  });

  return { weeks, filled, pending, skipped, otNights, unrostered, tsNights: tsNights.length, fallbackNights, partialNights };
}

// ── Night Sort Tab ─────────────────────────────────────────────────────────────

function NightTab({ form: formState, setForm, rates, rateNote }) {
  const form = { ...formState, rates: rates.night };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [importStatus, setImportStatus] = useState('');
  const [importing, setImporting] = useState(false);

  // Populate the whole grid from the Night Sorters app (Actual times data) for
  // the selected period. Overwrites the week tables; everything stays editable.
  const importFromApp = async () => {
    setImporting(true); setImportStatus('');
    try {
      const period = PERIODS.find(p => p.label === form.selectedPeriod) || findCurrentPeriod();
      const { weeks, filled, pending, skipped, otNights, unrostered, tsNights, fallbackNights, partialNights } = await buildWeeksFromActualTimes(period);
      setForm(f => ({ ...f, selectedPeriod: period.label, periodStart: period.startISO, weeks }));
      setImportStatus(
        `Billed ${filled} night${filled === 1 ? '' : 's'} for ${period.label} — the 30-min break is removed from every shift, so AP is not over-billed. ` +
        `${tsNights} night${tsNights === 1 ? '' : 's'} came from fully-approved per-sorter timesheets (span − actual break, the approvals Hrs). ` +
        (fallbackNights && fallbackNights.length
          ? `${fallbackNights.length} night${fallbackNights.length === 1 ? '' : 's'} had no per-sorter timesheets, so they came from the Actual-times ledger minus a flat 30-min break per shift (${fallbackNights.join(', ')}). `
          : `All billed nights came from per-sorter timesheets. `) +
        `Team Leader shifts bill at the leader rate; everyone else (including runners) bills as staff. ` +
        (partialNights && partialNights.length ? `⚠ ${partialNights.length} night${partialNights.length === 1 ? '' : 's'} had only SOME sorters approved (${partialNights.join(', ')}) — to avoid under-billing, the whole night was taken from the ledger. Approve the rest on Night Sorters → Approvals for exact break times, then re-import. ` : '') +
        (pending ? `${pending} per-sorter shift${pending === 1 ? '' : 's'} in this period are unapproved. ` : '') +
        (otNights.length
          ? `⚠ ${otNights.length} night${otNights.length === 1 ? '' : 's'} had shifts beyond 8 worked hours (${otNights.join(', ')}) — genuine O/T billed for the excess only. `
          : `No shift exceeded 8 worked hours, so no O/T is billed (long shifts under 8h bill at base rate only). `) +
        (unrostered.length ? `Note: ${unrostered.length} approved shift${unrostered.length === 1 ? '' : 's'} had no roster slot and were billed as staff (${unrostered.join(', ')}). ` : '') +
        (skipped.length ? `Skipped ${skipped.length} ledger night${skipped.length === 1 ? '' : 's'} with a missing start/finish or shift count: ${skipped.join(', ')} — fix these on Night Sorters → Actual times, then re-import.` : '')
      );
    } catch (e) { setImportStatus('Import failed: ' + e.message); }
    finally { setImporting(false); }
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const rows = parseDeputyCSV(ev.target.result);
      if (!rows || rows.length === 0) { setImportStatus('Could not read CSV — check the file format.'); return; }

      // Auto-detect period start from earliest date in CSV
      const dates = rows.map(r => r.date).sort();
      const earliest = dates[0]; // e.g. "2026-04-19"

      // Find the Sunday on or before earliest date
      const [y, m, d] = earliest.split('-').map(Number);
      const dt = new Date(y, m - 1, d);
      const dow = dt.getDay(); // 0=Sun
      const sundayOffset = -dow; // go back to Sunday
      const sunday = new Date(y, m - 1, d + sundayOffset);
      const sundayISO = `${sunday.getFullYear()}-${String(sunday.getMonth()+1).padStart(2,'0')}-${String(sunday.getDate()).padStart(2,'0')}`;

      const weeks = buildWeeksFromRows(rows, sundayISO);
      const staffRows  = rows.filter(r => r.area !== 'Team Leader').length;
      const leaderRows = rows.filter(r => r.area === 'Team Leader').length;

      setForm(f => ({ ...f, periodStart: sundayISO, weeks }));
      setImportStatus(`Imported ${rows.length} shifts (${staffRows} staff, ${leaderRows} team leader) — period start set to ${sundayISO}.`);
    };
    reader.readAsText(file);
    e.target.value = ''; // allow re-upload same file
  };

  const updateWeekCell = (wi, key, di, val) => setForm(f => {
    const weeks = f.weeks.map((w, i) => {
      if (i !== wi) return w;
      const arr = [...w[key]];
      arr[di] = val;
      return { ...w, [key]: arr };
    });
    return { ...f, weeks };
  });

  let grandTotal = 0;
  form.weeks.forEach(wk => {
    grandTotal += nightSplit(wk.staff,    form.rates, 'staff').due;
    grandTotal += nightSplit(wk.leader,   form.rates, 'leader').due;
    grandTotal += nightSplit(wk.staffOT,  form.rates, 'staffOT').due;
    grandTotal += nightSplit(wk.leaderOT, form.rates, 'leaderOT').due;
  });

  const gst = grandTotal * 0.1;
  const total = grandTotal + gst;

  const handlePrint = () => {
    saveInvoiceNum('night', form.invoiceNum);
    saveTaxInvoiceTotal('night', form, { subtotalEx: grandTotal, gst, total });
    printInvoice(nightHTML(form));
  };

  return (
    <div>
      {rateNote}
      {/* Import banner */}
      <div className="card" style={{ marginBottom: 14, background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: 'var(--hp-gold)', marginBottom: 3 }}>Populate from the app</div>
            <div style={{ fontSize: '0.82rem', color: 'var(--hp-muted)' }}>
              Pick the period, then import — start times, AP finish times, overtime and shift counts come straight from
              Night Sorters → Actual times. Generate on the final day of the period so the last week is in. (Deputy CSV upload still works as a fallback.)
            </div>
            {importStatus && (
              <div style={{ marginTop: 6, fontSize: '0.82rem', color: importStatus.startsWith('Import failed') ? 'var(--hp-danger, #f87171)' : 'var(--hp-success, #4ade80)', background: importStatus.startsWith('Import failed') ? 'rgba(248,113,113,0.08)' : 'rgba(74,222,128,0.08)', padding: '4px 10px', borderRadius: 4 }}>
                {importStatus}
              </div>
            )}
          </div>
          <button className="btn btn-primary" onClick={importFromApp} disabled={importing}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {importing ? 'Importing…' : '⤓ Import from app'}
          </button>
          <label style={{ cursor: 'pointer' }}>
            <input type="file" accept=".csv" onChange={handleCSVUpload} style={{ display: 'none' }} />
            <span className="btn btn-ghost" style={{ display: 'flex', alignItems: 'center', gap: 6, pointerEvents: 'none' }}>
              ↑ Deputy CSV
            </span>
          </label>
        </div>
      </div>

      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <Field label="Invoice Number">
          <input className="form-input" value={form.invoiceNum} onChange={e => set('invoiceNum', e.target.value)} placeholder="e.g. 583" />
        </Field>
        <Field label="Invoice Date">
          <input className="form-input" type="text" value={form.invoiceDate} onChange={e => set('invoiceDate', e.target.value)} placeholder="e.g. 15-May-2026" />
        </Field>
        <PeriodSelector form={form} setForm={setForm} autoFillDates={(f, p) => ({
          ...f,
          selectedPeriod: p.label,
          periodStart: p.startISO,
        })} />
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ fontWeight: 600, marginBottom: 10, color: 'var(--hp-gold)' }}>Rates (Ex GST) — Mon–Sat nights · from the Rates tab</div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
          {[
            { key: 'staff',    label: 'Staff / hr' },
            { key: 'leader',   label: 'Leader / hr' },
            { key: 'staffOT',  label: 'Staff O/T' },
            { key: 'leaderOT', label: 'Leader O/T' },
            { key: 'parcels',  label: 'Parcels' },
          ].map(({ key, label }) => (
            <Field key={key} label={label}>
              <input className="form-input" readOnly value={fmtRate(form.rates[key])} title="Change on the Rates tab" style={{ background: 'transparent' }} />
            </Field>
          ))}
        </div>
        <div style={{ fontWeight: 600, margin: '14px 0 10px', color: 'var(--hp-gold)' }}>Sunday-night rates (Ex GST) — the ★ Sunday column</div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
          {[
            { key: 'staffSun',    label: 'Staff / hr (Sun)' },
            { key: 'leaderSun',   label: 'Leader / hr (Sun)' },
            { key: 'staffSunOT',  label: 'Staff O/T (Sun)' },
            { key: 'leaderSunOT', label: 'Leader O/T (Sun)' },
          ].map(({ key, label }) => (
            <Field key={key} label={label}>
              <input className="form-input" readOnly value={fmtRate(form.rates[key])} title="Change on the Rates tab" style={{ background: 'transparent' }} />
            </Field>
          ))}
          <div style={{ fontSize: '0.74rem', color: 'var(--hp-muted)', alignSelf: 'end', paddingBottom: 6, lineHeight: 1.5 }}>
            These are the saved rates for this period. Change them on the Rates tab — never on this device.
          </div>
        </div>
      </div>

      {form.weeks.map((wk, wi) => (
        <NightWeekTable key={wi} weekIdx={wi} week={wk} periodStart={form.periodStart} rates={form.rates} onChange={updateWeekCell} />
      ))}

      <div className="card card-gold" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '1.05rem' }}>
          <span style={{ color: 'var(--hp-muted)', marginRight: 16 }}>Total Ex GST: {fmt(grandTotal)}</span>
          <span style={{ color: 'var(--hp-muted)', marginRight: 16 }}>GST: {fmt(gst)}</span>
          <strong style={{ color: 'var(--hp-gold)', fontSize: '1.15rem' }}>Total Inc GST: {fmt(total)}</strong>
        </div>
        <button className="btn btn-primary" onClick={handlePrint} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Printer size={16} /> Print / Save PDF
        </button>
      </div>
    </div>
  );
}


// ── Additional time (outside the contracted sort window) ──────────────────────
// AP contracts the sort window itself and pays it as a LINE ITEM: Sunday 23:15–06:15,
// Mon–Thu 23:45–06:15 (Paul, 4 Sep 2026). Time worked before that start or past that
// finish is additional, billed on its own invoice, and NO contracted hour may appear on
// it — the window's own hours are already paid by the line item. The pre-start half
// matters most in Peak, when the crew is pulled in early: the roster moves with AP's
// instruction, so only a fixed contract window still shows the extra.
//
// Baselines, in the operator's hands because each answers a different question:
//   'contract' — the contracted window above, applied to every shift whatever their
//                slot says. What AP signed; survives a peak re-roster. Default.
//                Runner slots run an hour later than the sort window, so whether their
//                last hour is contracted or additional is a commercial call:
//                shiftLaterSlots moves their window with them when it is contracted.
//   'slot'     — each position's own agreed hours from the slot register (runners
//                00:45–07:15), Sunday brought forward. Treats every slot as contracted.
//   'roster'   — that night's published roster row. Catches a person kept past OUR plan.
const EXTRA_GRACE_DEFAULT = 10;      // minutes; matches the Approvals late/early flag tolerance
const EXTRA_SUNDAY_SHIFT  = 30;      // the Sunday-start night begins this many minutes earlier
const EXTRA_MAX_DRIFT_MIN = 480;     // beyond 8h either way the times are wrong, not extra work
// The contracted sort window as AP pays it. Editable on the page; these are the defaults.
const EXTRA_CONTRACT = { sunStart: '23:15', sunEnd: '06:15', wkStart: '23:45', wkEnd: '06:15' };

// Minutes after 18:00, wrapping past midnight — the same night-shift clock the
// Approvals ledger uses, so 06:30 reads as later than 06:15 and 23:15 as earlier
// than 23:45 without any date arithmetic.
function nightMinOf(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec((hhmm || '').toString().trim());
  if (!m) return null;
  return (((+m[1]) * 60 + (+m[2])) - 1080 + 1440) % 1440;
}
function nightMinToHHmm(n) {
  const mins = (((n % 1440) + 1440) % 1440 + 1080) % 1440;
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}
const isLeaderSlot = (slot) => /team leader/i.test(slot || '');   // same test as the Night Sort grid
// "7.50" prices the line; "7 h 30 m" is what was actually counted off the clock.
function hoursMins(h) {
  const m = Math.round((h || 0) * 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} m`;
}

// Rate for one claim line: base hours always, plus the O/T half-rate on any of those
// hours that fall beyond 8 worked in the shift — the same workplace-law basis as the
// Night Sort invoice, so the two never disagree about what overtime is.
function extraLineAmount(line, rates) {
  const bk = line.isLeader ? 'leader' : 'staff';
  const ok = line.isLeader ? 'leaderOT' : 'staffOT';
  const pick = (k) => (line.isSun && rates[NIGHT_SUN_RATE[k]] != null ? rates[NIGHT_SUN_RATE[k]] : rates[k]) || 0;
  return line.hours * pick(bk) + (line.otHours || 0) * pick(ok);
}
const extraRateOf = (line, rates) => {
  const bk = line.isLeader ? 'leader' : 'staff';
  return (line.isSun && rates[NIGHT_SUN_RATE[bk]] != null ? rates[NIGHT_SUN_RATE[bk]] : rates[bk]) || 0;
};
const extraOtRateOf = (line, rates) => {
  const ok = line.isLeader ? 'leaderOT' : 'staffOT';
  return (line.isSun && rates[NIGHT_SUN_RATE[ok]] != null ? rates[NIGHT_SUN_RATE[ok]] : rates[ok]) || 0;
};

// Scan one period for time worked outside the window. Reads the SAME approved
// timesheets the Night Sort invoice bills from (getSorterTimesheetsAdmin already
// resolves each row to its roster slot and returns the slot templates), so a claim
// here can always be traced back to a shift on Night Sorters → Approvals.
async function scanAdditionalTime(period, opts) {
  const { baseline = 'contract', graceMins = EXTRA_GRACE_DEFAULT, sundayShiftMin = EXTRA_SUNDAY_SHIFT,
          shiftLaterSlots = false, ...win } = opts || {};
  const C = { ...EXTRA_CONTRACT, ...Object.fromEntries(Object.keys(EXTRA_CONTRACT).map(k => [k, win[k] || EXTRA_CONTRACT[k]])) };
  const from = period.startISO, to = period.endISO;
  const t = await nsApi('getSorterTimesheetsAdmin', { status: 'All', from, to, limit: 2000 });
  if (!t || !t.success) throw new Error((t && t.error) || 'Could not load the sorter timesheets');

  const slotDef = {};
  (t.slots || []).forEach(s => { slotDef[s.slot] = { start: s.start || '', end: s.end || '' }; });
  // How much later than the sort itself each position starts, taken from the slot register
  // rather than the slot NAME — "Sorter City 4 - Used when TL is Runner" reads as a runner
  // and is not one, it works sorter hours. The commonest start across the register IS the
  // sort window, so everything measures from there and only the genuinely later slots
  // (Runner 1/2, Team Leader / Runner) come out non-zero.
  const startTally = {};
  Object.values(slotDef).forEach(d => { if (d.start) startTally[d.start] = (startTally[d.start] || 0) + 1; });
  const baseSlotStart = Object.keys(startTally).sort((x, y) => startTally[y] - startTally[x])[0] || '';
  const slotOffsetMin = (slot) => {
    const d = slotDef[slot];
    if (!d || !d.start || !baseSlotStart) return 0;
    return Math.max(0, nightMinOf(d.start) - nightMinOf(baseSlotStart));
  };

  const claims = [];
  const unchecked = [];          // shifts with no window to measure against — never silently dropped
  const crewByNight = {};        // shifts examined per night, so a whole-crew hold-over is recognisable
  let checked = 0, pending = 0;

  (t.timesheets || []).forEach(r => {
    const status = (r.Status || '').toString();
    if (status === 'Discarded' || status === 'Missing' || status === 'Absent') return;
    const date = (r.Date || '').toString();
    if (!date || date < from || date > to) return;
    if (!r.actualIn || !r.actualOut) return;                       // still on shift / never closed

    const slot = r.slot || r.rosterSlot || '';
    const [yy, mm, dd] = date.split('-').map(Number);
    const isSun = new Date(yy, mm - 1, dd).getDay() === 0;

    let ws = '', we = '';
    if (baseline === 'roster') { ws = r.rosteredStart || ''; we = r.rosteredEnd || ''; }
    else if (baseline === 'slot') {
      const d = slotDef[slot] || { start: r.rosteredStart || '', end: r.rosteredEnd || '' };
      ws = d.start; we = d.end;
      // The Sunday-start night runs 30 min earlier across every slot, so shifting the
      // slot's weeknight start keeps Sunday from reading as a crew-wide early start.
      if (isSun && ws && sundayShiftMin) ws = nightMinToHHmm(nightMinOf(ws) - sundayShiftMin);
    } else {
      ws = isSun ? C.sunStart : C.wkStart;
      we = isSun ? C.sunEnd : C.wkEnd;
      // Runner slots work the same span an hour later than the sort. Left alone, that
      // late hour falls OUTSIDE the contracted window and bills as additional; shifting
      // their window treats it as contracted instead. A commercial call, so it is a toggle.
      const off = shiftLaterSlots ? slotOffsetMin(slot) : 0;
      if (off) { ws = nightMinToHHmm(nightMinOf(ws) + off); we = nightMinToHHmm(nightMinOf(we) + off); }
    }
    if (!ws || !we) { unchecked.push(`${r.Login} ${date}`); return; }

    checked++;
    crewByNight[date] = (crewByNight[date] || 0) + 1;

    let lateM = nightMinOf(r.actualOut) - nightMinOf(we);
    let earlyM = nightMinOf(ws) - nightMinOf(r.actualIn);
    if (Math.abs(lateM) > EXTRA_MAX_DRIFT_MIN) lateM = 0;
    if (Math.abs(earlyM) > EXTRA_MAX_DRIFT_MIN) earlyM = 0;
    if (lateM <= graceMins && earlyM <= graceMins) return;

    const approved = status === 'Approved';
    if (!approved) pending++;
    const worked = Number(r.workedHours) || 0;
    // Hours beyond 8 are the LAST hours worked, so the overrun carries the O/T
    // loading first and only what is left of it reaches the early start.
    let otLeft = Math.max(0, worked - 8);
    const mk = (kind, mins, sched, actual) => {
      const hours = Math.round((mins / 60) * 100) / 100;
      const otHours = Math.round(Math.min(otLeft, hours) * 100) / 100;
      otLeft = Math.round((otLeft - otHours) * 100) / 100;
      claims.push({
        id: `${r.ID}|${kind}`, date, kind, mins, hours, otHours,
        login: r.Login || '', name: r.Name || r.Login || '', slot,
        sched, actual, worked, approved, status,
        isLeader: isLeaderSlot(slot), isSun,
        // An unapproved shift is shown but never ticked: the Night Sort invoice
        // won't bill it either, and an unapproved time can still change.
        include: approved,
      });
    };
    if (lateM > graceMins) mk('after', lateM, we, r.actualOut);
    if (earlyM > graceMins) mk('before', earlyM, ws, r.actualIn);
  });

  claims.sort((a, b) => (a.date === b.date
    ? (a.kind === b.kind ? a.name.localeCompare(b.name) : (a.kind === 'before' ? -1 : 1))
    : a.date.localeCompare(b.date)));

  // A night where every shift examined ran over (or every shift started early) is a
  // collective hold-over, not a handful of individuals — the invoice says so.
  const collective = {};
  ['before', 'after'].forEach(kind => {
    Object.keys(crewByNight).forEach(date => {
      const n = claims.filter(c => c.date === date && c.kind === kind).length;
      if (n > 1 && n >= crewByNight[date]) (collective[date] = collective[date] || {})[kind] = n;
    });
  });

  return { claims, collective, crewByNight, unchecked, checked, pending, nights: Object.keys(crewByNight).length };
}

// Additional-time invoice. One row per claim (who, which night, the contracted
// time vs the time actually worked) so AP can check every line against the sort
// sheets; nights the whole crew was held over are labelled as such.
function extraHTML(form) {
  const { invoiceNum, invoiceDate, rates, selectedPeriod } = form;
  const lines = (form.claims || []).filter(c => c.include);
  const KIND = { after: 'Worked past the finish', before: 'Started before the start' };

  let subtotal = 0;
  const byNight = [];
  lines.forEach(c => {
    const n = byNight.find(x => x.date === c.date);
    (n || byNight[byNight.push({ date: c.date, rows: [] }) - 1]).rows.push(c);
  });

  const body = byNight.map(({ date, rows }) => {
    const coll = (form.collective || {})[date] || {};
    const tags = Object.keys(coll).map(k => `${KIND[k].toLowerCase()} — whole crew (${coll[k]} shifts)`).join(' · ');
    const nightRows = rows.map((c, i) => {
      const rate = extraRateOf(c, rates), otRate = extraOtRateOf(c, rates);
      const amt = extraLineAmount(c, rates);
      subtotal += amt;
      const bg = i % 2 === 0 ? '#faf9f6' : '#fff';
      const ot = c.otHours > 0
        ? `<tr style="background:${bg}">
             <td style="padding:4px 8px 4px 22px;color:#666">— O/T loading (hours beyond 8 worked)</td>
             <td colspan="3"></td>
             <td style="padding:4px 6px;text-align:center">${c.otHours.toFixed(2)}</td>
             <td style="padding:4px 6px;text-align:right">${fmt(otRate)}</td>
             <td style="padding:4px 6px;text-align:right">${fmt(c.otHours * otRate)}</td>
           </tr>` : '';
      return `
      <tr style="background:${bg}">
        <td style="padding:5px 8px">${c.name}${c.slot ? `<div style="color:#888;font-size:8px">${c.slot}</div>` : ''}</td>
        <td style="padding:5px 6px">${KIND[c.kind]}<div style="color:#888;font-size:8px">${c.mins} min</div></td>
        <td style="padding:5px 6px;text-align:center">${c.sched}</td>
        <td style="padding:5px 6px;text-align:center">${c.actual}</td>
        <td style="padding:5px 6px;text-align:center">${c.hours.toFixed(2)}</td>
        <td style="padding:5px 6px;text-align:right">${fmt(rate)}</td>
        <td style="padding:5px 6px;text-align:right">${fmt(c.hours * rate)}</td>
      </tr>${ot}`;
    }).join('');
    return `
    <tr style="background:#efece5">
      <td colspan="7" style="padding:5px 8px;font-weight:700">
        ${dayName(date)} ${formatMedium(date)}${tags ? ` <span style="font-weight:400;color:#666">· ${tags}</span>` : ''}
      </td>
    </tr>${nightRows}`;
  }).join('');

  const totalHours = lines.reduce((a, c) => a + c.hours, 0);
  const gst = subtotal * 0.1;
  const total = subtotal + gst;

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
${BASE_CSS}
body { font-size: 9.5px; }
</style></head><body>
${invoiceHead(NIGHT_RECIPIENT, invoiceNum, fmtInvDate(invoiceDate), selectedPeriod || '')}

<div style="margin-bottom:10px;font-size:10.5px">
  <strong>Additional time — night sort</strong><br>
  <span style="color:#555">Time worked outside the contracted sort window during ${selectedPeriod || 'the period'}. ${
    form.baseline === 'contract'
      ? `Contracted window: Sunday ${form.sunStart}–${form.sunEnd}, Mon–Thu ${form.wkStart}–${form.wkEnd}${form.shiftLaterSlots ? ' (runner positions one hour later)' : ''}. Those hours are billed as the sort line item and are excluded here.`
      : 'Contracted hours are billed as the sort line item and are excluded here.'} ${lines.length} claim${lines.length === 1 ? '' : 's'} across ${byNight.length} night${byNight.length === 1 ? '' : 's'}.</span>
</div>

<table class="items-table">
  <thead>
    <tr>
      <th style="width:24%">Sorter</th>
      <th>Additional time</th>
      <th style="text-align:center">Scheduled</th>
      <th style="text-align:center">Actual</th>
      <th style="text-align:center">Hours</th>
      <th style="text-align:right">Rate</th>
      <th style="text-align:right">Amount Ex GST</th>
    </tr>
  </thead>
  <tbody>
    ${body || '<tr><td colspan="7" style="padding:10px;text-align:center;color:#888">No additional time claimed.</td></tr>'}
    <tr class="trow-sub">
      <td style="padding:6px 8px">Total additional hours</td>
      <td colspan="2"></td>
      <td style="padding:6px;text-align:center;font-weight:400;color:#555">${hoursMins(totalHours)}</td>
      <td style="padding:6px;text-align:center">${(Math.round(totalHours * 100) / 100).toFixed(2)}</td>
      <td></td>
      <td style="padding:6px;text-align:right">${fmt(subtotal)}</td>
    </tr>
  </tbody>
</table>

<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-top:14px">
  <div style="flex:1">
    <div class="paybox">
      <strong>Payment Details</strong><br>
      BSB: ${COMPANY.bsb} &nbsp;·&nbsp; ACC: ${COMPANY.acc}<br>
      Please quote invoice number <strong>${invoiceNum || ''}</strong> as your payment reference.
    </div>
    <div class="terms">Terms as per schedule · Hollypaul Engine · ABN ${COMPANY.abn}</div>
  </div>
  <table style="width:270px;border:1px solid #e3e0d8;font-size:10.5px;border-collapse:collapse;margin-left:16px">
    <tr>
      <td style="padding:6px 10px;background:#f6f4ee;font-weight:600">Subtotal (ex GST)</td>
      <td style="padding:6px 10px;background:#f6f4ee;text-align:right;font-weight:600">${fmt(subtotal)}</td>
    </tr>
    <tr>
      <td style="padding:6px 10px;background:#f6f4ee;color:#555">GST 10.00%</td>
      <td style="padding:6px 10px;background:#f6f4ee;text-align:right;color:#555">${fmt(gst)}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px;background:#141414;color:#C9A84C;font-weight:700;font-size:11.5px">Total Due (inc GST)</td>
      <td style="padding:8px 10px;background:#141414;color:#C9A84C;font-weight:700;font-size:11.5px;text-align:right">${fmt(total)}</td>
    </tr>
  </table>
</div>
</body></html>`;
}

function makeExtraForm() {
  const today = new Date().toISOString().slice(0, 10);
  const cp = findCurrentPeriod();
  return {
    invoiceNum: getNextInvoiceNum('extra'),
    invoiceDate: today,
    selectedPeriod: cp.label,
    periodStart: cp.startISO,
    periodEnd: cp.endISO,
    baseline: 'contract',
    graceMins: EXTRA_GRACE_DEFAULT,
    sundayShiftMin: EXTRA_SUNDAY_SHIFT,
    ...EXTRA_CONTRACT,
    shiftLaterSlots: true,
    claims: [],
    collective: {},
    scan: null,
  };
}

const BASELINES = [
  { key: 'contract', label: 'Contracted window', hint: 'The window AP pays as the sort line item, below. Applied to every shift however it was rostered — so a peak roster that pulls the crew in early still shows the extra.' },
  { key: 'slot',     label: "Each position's own hours", hint: 'The agreed hours held against each position in the slot register (runners 00:45–07:15), Sunday brought forward.' },
  { key: 'roster',   label: "That night's roster",       hint: 'The published roster row for the person. Catches anyone held past the time we rostered them.' },
];

function ExtraTab({ form: formState, setForm, rates, rateNote }) {
  const form = { ...formState, rates: rates.extra };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState('');

  const runCheck = async () => {
    setChecking(true); setStatus('');
    try {
      const period = PERIODS.find(p => p.label === form.selectedPeriod) || findCurrentPeriod();
      const res = await scanAdditionalTime(period, {
        baseline: form.baseline,
        graceMins: Number(form.graceMins) || 0,
        sundayShiftMin: Number(form.sundayShiftMin) || 0,
        shiftLaterSlots: form.shiftLaterSlots,
        sunStart: form.sunStart, sunEnd: form.sunEnd, wkStart: form.wkStart, wkEnd: form.wkEnd,
      });
      setForm(f => ({ ...f, selectedPeriod: period.label, periodStart: period.startISO, periodEnd: period.endISO,
        claims: res.claims, collective: res.collective, scan: res }));
      const after = res.claims.filter(c => c.kind === 'after').length;
      const before = res.claims.filter(c => c.kind === 'before').length;
      setStatus(res.claims.length
        ? `${res.claims.length} claim${res.claims.length === 1 ? '' : 's'} across ${res.nights} night${res.nights === 1 ? '' : 's'} checked — ${after} past the finish, ${before} before the start.`
          + (Object.keys(res.collective).length ? ` ${Object.keys(res.collective).length} whole-crew night${Object.keys(res.collective).length === 1 ? '' : 's'}.` : '')
          + (res.pending ? ` ⚠ ${res.pending} of them are not approved yet — they are listed but left unticked; approve them on Night Sorters → Approvals first.` : '')
          + (res.unchecked.length ? ` ${res.unchecked.length} shift${res.unchecked.length === 1 ? '' : 's'} had no window to measure against (${res.unchecked.slice(0, 6).join(', ')}${res.unchecked.length > 6 ? '…' : ''}).` : '')
        : `Nothing outside the window — all ${res.checked} shift${res.checked === 1 ? '' : 's'} across ${res.nights} night${res.nights === 1 ? '' : 's'} started and finished inside the contracted times (allowing ${form.graceMins} min).`);
    } catch (e) { setStatus('Check failed: ' + e.message); }
    finally { setChecking(false); }
  };

  const toggle = (id) => setForm(f => ({ ...f, claims: f.claims.map(c => c.id === id ? { ...c, include: !c.include } : c) }));
  const setAll = (on) => setForm(f => ({ ...f, claims: f.claims.map(c => ({ ...c, include: on && c.approved })) }));

  const picked = form.claims.filter(c => c.include);
  const hours = Math.round(picked.reduce((a, c) => a + c.hours, 0) * 100) / 100;
  const subtotal = picked.reduce((a, c) => a + extraLineAmount(c, form.rates), 0);
  const gst = subtotal * 0.1;
  const total = subtotal + gst;
  const nights = new Set(picked.map(c => c.date)).size;

  const handlePrint = () => {
    if (!picked.length) { alert('Nothing ticked to invoice.'); return; }
    saveInvoiceNum('extra', form.invoiceNum);
    saveTaxInvoiceTotal('extra', form, { subtotalEx: subtotal, gst, total, quantity: hours });
    printInvoice(extraHTML(form));
  };

  const KIND = { after: 'Past the finish', before: 'Before the start' };

  return (
    <div>
      {rateNote}
      {/* Check */}
      <div className="card" style={{ marginBottom: 14, background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.25)' }}>
        <div style={{ fontWeight: 600, color: 'var(--hp-gold)', marginBottom: 3 }}>Did we work outside the sort window?</div>
        <div style={{ fontSize: '0.82rem', color: 'var(--hp-muted)', marginBottom: 10 }}>
          Checks every approved night-sort shift in the period against the contracted window — Sunday 23:15–06:15, Mon–Thu
          23:45–06:15 — and claims only what falls outside it, before the start or past the finish. One person or the whole
          crew. Contracted hours are AP's sort line item and never appear here.
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--hp-muted)', marginBottom: 10, padding: '6px 10px', borderRadius: 4,
          background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--hp-danger, #f87171)' }}>The Night Sort invoice still bills actual hours.</strong> Until it is
          switched to bill the contracted window as a flat line item, the hours below sit on that invoice too — raising both
          unchanged sends the same hour to AP twice.
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, alignItems: 'end' }}>
          <PeriodSelector form={form} setForm={setForm} autoFillDates={(f, p) => ({
            ...f, selectedPeriod: p.label, periodStart: p.startISO, periodEnd: p.endISO, claims: [], collective: {}, scan: null,
          })} />
          <Field label="Ignore under (mins)">
            <input className="form-input" type="number" min="0" step="1" value={form.graceMins} onChange={e => set('graceMins', e.target.value)} />
          </Field>
          <button className="btn btn-primary" onClick={runCheck} disabled={checking} style={{ height: 38 }}>
            {checking ? 'Checking…' : '🔍 Check the period'}
          </button>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {BASELINES.map(b => (
            <label key={b.key} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer', maxWidth: 260, fontSize: '0.82rem' }}>
              <input type="radio" name="extra-baseline" checked={form.baseline === b.key} onChange={() => set('baseline', b.key)} style={{ marginTop: 3 }} />
              <span>
                <span style={{ fontWeight: 600, color: form.baseline === b.key ? 'var(--hp-gold)' : 'inherit' }}>{b.label}</span>
                <span style={{ display: 'block', color: 'var(--hp-muted)', fontSize: '0.74rem', lineHeight: 1.45 }}>{b.hint}</span>
              </span>
            </label>
          ))}
        </div>
        {form.baseline === 'contract' && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(128,128,128,0.15)' }}>
            <div style={{ fontSize: '0.78rem', color: 'var(--hp-muted)', marginBottom: 8 }}>
              The contracted sort window — the hours AP already pays as the sort line item. Nothing inside it can reach this invoice.
            </div>
            <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, maxWidth: 620 }}>
              <Field label="Sunday start"><input className="form-input" type="time" value={form.sunStart} onChange={e => set('sunStart', e.target.value)} /></Field>
              <Field label="Sunday finish"><input className="form-input" type="time" value={form.sunEnd} onChange={e => set('sunEnd', e.target.value)} /></Field>
              <Field label="Mon–Thu start"><input className="form-input" type="time" value={form.wkStart} onChange={e => set('wkStart', e.target.value)} /></Field>
              <Field label="Mon–Thu finish"><input className="form-input" type="time" value={form.wkEnd} onChange={e => set('wkEnd', e.target.value)} /></Field>
            </div>
            <label style={{ display: 'flex', gap: 7, alignItems: 'flex-start', marginTop: 10, cursor: 'pointer', fontSize: '0.8rem', maxWidth: 620 }}>
              <input type="checkbox" checked={form.shiftLaterSlots} onChange={e => set('shiftLaterSlots', e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                Runner positions keep their own window, an hour later
                <span style={{ display: 'block', color: 'var(--hp-muted)', fontSize: '0.74rem', lineHeight: 1.45 }}>
                  Runners are rostered 00:45–07:15 (00:15–07:15 Sunday), so their last hour sits outside the window above.
                  Ticked, that hour counts as contracted. Unticked, it bills as additional every night — which is the bigger claim.
                </span>
              </span>
            </label>
          </div>
        )}
        {form.baseline === 'slot' && (
          <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'end', maxWidth: 320 }}>
            <Field label="Sunday night starts earlier by (mins)">
              <input className="form-input" type="number" min="0" step="5" value={form.sundayShiftMin} onChange={e => set('sundayShiftMin', e.target.value)} />
            </Field>
          </div>
        )}

        {status && (
          <div style={{ marginTop: 10, fontSize: '0.82rem', padding: '5px 10px', borderRadius: 4,
            color: status.startsWith('Check failed') ? 'var(--hp-danger, #f87171)' : 'var(--hp-success, #4ade80)',
            background: status.startsWith('Check failed') ? 'rgba(248,113,113,0.08)' : 'rgba(74,222,128,0.08)' }}>
            {status}
          </div>
        )}
      </div>

      {/* Invoice details */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          <Field label="Invoice No."><input className="form-input" value={form.invoiceNum} onChange={e => set('invoiceNum', e.target.value)} /></Field>
          <Field label="Invoice Date"><input className="form-input" type="date" value={form.invoiceDate} onChange={e => set('invoiceDate', e.target.value)} /></Field>
          <Field label="Staff / hr — Rates tab"><input className="form-input" readOnly value={fmtRate(form.rates.staff)} title="Change on the Rates tab" style={{ background: 'transparent' }} /></Field>
          <Field label="Leader / hr — Rates tab"><input className="form-input" readOnly value={fmtRate(form.rates.leader)} title="Change on the Rates tab" style={{ background: 'transparent' }} /></Field>
        </div>
        <div style={{ fontSize: '0.74rem', color: 'var(--hp-muted)', marginTop: 8, lineHeight: 1.5 }}>
          Rates are the saved Additional Time rates for this period (Rates tab; Sunday nights bill at the Sunday rates). Hours beyond 8 in a shift
          carry the O/T loading on top, exactly as on the Night Sort invoice.
        </div>
      </div>

      {/* Claims */}
      {form.claims.length > 0 && (
        <div className="card" style={{ marginBottom: 14, overflowX: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 600, color: 'var(--hp-gold)' }}>
              Additional time found — {nights} night{nights === 1 ? '' : 's'}, {hoursMins(hours)} ticked ({hours} h)
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setAll(true)}>Tick all</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setAll(false)}>Untick all</button>
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(128,128,128,0.25)', textAlign: 'left', color: 'var(--hp-muted)' }}>
                <th style={{ padding: '6px 4px', width: 34 }}></th>
                <th style={{ padding: '6px 4px' }}>Night</th>
                <th style={{ padding: '6px 4px' }}>Sorter</th>
                <th style={{ padding: '6px 4px' }}>Position</th>
                <th style={{ padding: '6px 4px' }}>Additional</th>
                <th style={{ padding: '6px 4px', textAlign: 'center' }}>Scheduled</th>
                <th style={{ padding: '6px 4px', textAlign: 'center' }}>Actual</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Mins</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Hrs</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {form.claims.map(c => {
                const coll = (form.collective[c.date] || {})[c.kind];
                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid rgba(128,128,128,0.12)', opacity: c.include ? 1 : 0.5 }}>
                    <td style={{ padding: '5px 4px' }}>
                      <input type="checkbox" checked={c.include} onChange={() => toggle(c.id)} />
                    </td>
                    <td style={{ padding: '5px 4px', whiteSpace: 'nowrap' }}>
                      {dayName(c.date).slice(0, 3)} {formatMedium(c.date)}
                      {c.isSun && <span style={{ color: 'var(--hp-gold)' }}> ★</span>}
                    </td>
                    <td style={{ padding: '5px 4px' }}>
                      {c.name}
                      {!c.approved && <span style={{ color: 'var(--hp-danger, #f87171)', fontSize: '0.72rem' }}> · {c.status || 'not approved'}</span>}
                    </td>
                    <td style={{ padding: '5px 4px', color: 'var(--hp-muted)' }}>{c.slot}</td>
                    <td style={{ padding: '5px 4px', whiteSpace: 'nowrap' }}>
                      {KIND[c.kind]}
                      {coll && <span style={{ marginLeft: 6, fontSize: '0.7rem', padding: '1px 6px', borderRadius: 8, whiteSpace: 'nowrap', background: 'rgba(201,168,76,0.18)', color: 'var(--hp-gold)' }}>whole crew</span>}
                    </td>
                    <td style={{ padding: '5px 4px', textAlign: 'center' }}>{c.sched}</td>
                    <td style={{ padding: '5px 4px', textAlign: 'center', fontWeight: 600 }}>{c.actual}</td>
                    <td style={{ padding: '5px 4px', textAlign: 'right' }}>{c.mins}</td>
                    <td style={{ padding: '5px 4px', textAlign: 'right' }}>{c.hours.toFixed(2)}</td>
                    <td style={{ padding: '5px 4px', textAlign: 'right' }}>{fmt(extraLineAmount(c, form.rates))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {form.scan && form.claims.length === 0 && (
        <div className="card" style={{ marginBottom: 14, textAlign: 'center', color: 'var(--hp-muted)' }}>
          Nothing to invoice — every shift in this period stayed inside the window.
        </div>
      )}

      <div className="card card-gold" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: '1.05rem' }}>
          <span style={{ color: 'var(--hp-muted)', marginRight: 16 }}>{hoursMins(hours)} additional ({hours} h)</span>
          <span style={{ color: 'var(--hp-muted)', marginRight: 16 }}>Total Ex GST: {fmt(subtotal)}</span>
          <span style={{ color: 'var(--hp-muted)', marginRight: 16 }}>GST: {fmt(gst)}</span>
          <strong style={{ color: 'var(--hp-gold)', fontSize: '1.15rem' }}>Total Inc GST: {fmt(total)}</strong>
        </div>
        <button className="btn btn-primary" onClick={handlePrint} disabled={!picked.length} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Printer size={16} /> Print / Save PDF
        </button>
      </div>
    </div>
  );
}

// ── Rates Tab ──────────────────────────────────────────────────────────────────
// The one place rates are changed. Each save appends a version (effective-from date)
// server-side; invoices pick the version in force at their period start, so old
// periods keep their old rates and nothing on this device can override them.

const RATE_SECTIONS = [
  { type: 'doe',   title: 'DoE — Dept of Education', hint: 'Main line bills per day; every school / location shares the one school rate.' },
  { type: 'wave',  title: '2nd Wave EP', hint: 'Per item, plus the per-item summary adjustment.' },
  { type: 'night', title: 'Night Sort', hint: 'Mon–Sat nights, and the ★ Sunday-start night at its own rates.' },
  { type: 'extra', title: 'Additional Time', hint: 'Time outside the contracted window. O/T is the loading on top for hours beyond 8.' },
];

function fmtStamp(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function RatesTab({ store, editor, setEditor, dirty, saving, saveMsg, onSave, onDiscard, onRetry, deviceRates, onUseDevice, onDropDevice,
  onApplyCpi, onUndoCpi, cpiUndos, onUndoSave }) {
  const latest = latestVersion(store.versions);
  const prior = versionBefore(store.versions);
  const [cpiPct, setCpiPct] = useState('');
  const [cpiTypes, setCpiTypes] = useState(RATE_TYPES);
  const toggleCpiType = (t) => setCpiTypes(ts => ts.includes(t) ? ts.filter(x => x !== t) : [...ts, t]);
  const setRate = (type, key, val) => setEditor(e => ({ ...e, rates: { ...e.rates, [type]: { ...e.rates[type], [key]: val } } }));
  const history = sortVersions(store.versions).reverse();

  return (
    <div>
      {store.status === 'error' && (
        <div className="card" style={{ marginBottom: 14, borderColor: 'var(--hp-danger)', background: 'rgba(248,113,113,0.08)' }}>
          <strong style={{ color: 'var(--hp-danger)' }}>Saved rates could not load ({store.error}).</strong> Saving is off until they do,
          so nothing is written over rates this page can't see. <button className="btn btn-sm btn-secondary" onClick={onRetry} style={{ marginLeft: 8 }}>Retry</button>
        </div>
      )}

      {deviceRates && (
        <div className="card" style={{ marginBottom: 14, background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.35)' }}>
          <div style={{ fontWeight: 600, color: 'var(--hp-gold)', marginBottom: 6 }}>This browser held its own Night Sort rates</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--hp-muted)', marginBottom: 8 }}>
            They are no longer used. The saved rates below always win. Differences:{' '}
            {RATE_FIELDS.night.filter(({ key }) => deviceRates[key] != null && Number(deviceRates[key]) !== Number(editor.rates.night[key]))
              .map(({ key, label }) => `${label} ${fmtRate(deviceRates[key])} (saved ${fmtRate(editor.rates.night[key])})`).join(' · ')}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm btn-secondary" onClick={onUseDevice}>Put them in the editor (then Save)</button>
            <button className="btn btn-sm btn-ghost" onClick={onDropDevice}>Discard this browser's copy</button>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 14, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'end' }}>
        <Field label="Effective from (invoice periods starting on/after)">
          <input className="form-input" type="date" value={editor.effectiveFrom}
            onChange={e => setEditor(x => ({ ...x, effectiveFrom: e.target.value }))} />
        </Field>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Field label="Note (optional)">
            <input className="form-input" value={editor.note} placeholder="e.g. Fee increase agreed with AP"
              onChange={e => setEditor(x => ({ ...x, note: e.target.value }))} />
          </Field>
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--hp-muted)', paddingBottom: 6 }}>
          {store.status === 'loading' ? 'Loading saved rates…'
            : latest ? <>Last saved <strong>{fmtStamp(latest.savedAt)}</strong>{latest.savedBy ? ` by ${latest.savedBy}` : ''} · in force from {formatMedium(latest.effectiveFrom)}</>
            : store.status === 'ok' ? <strong style={{ color: 'var(--hp-danger)' }}>Nothing saved yet — press Save rates.</strong> : null}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ fontWeight: 600, color: 'var(--hp-gold)', marginBottom: 2 }}>CPI rate increase</div>
        <div style={{ fontSize: '0.76rem', color: 'var(--hp-muted)', marginBottom: 10 }}>
          Raises every rate in the ticked invoices by the percentage (DoE / EP to 4 dp, hourly rates to 2 dp; the negative
          adjustments scale too, so each net rate rises by the same %). It only fills the editor below — check the
          figures and the effective-from date, then press Save rates.
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
          <Field label="Increase %">
            <input className="form-input" type="number" step="0.01" value={cpiPct} placeholder="e.g. 3.2"
              onChange={e => setCpiPct(e.target.value)} style={{ width: 110 }} />
          </Field>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingBottom: 8, fontSize: '0.85rem' }}>
            {RATE_SECTIONS.map(({ type, title }) => (
              <label key={type} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={cpiTypes.includes(type)} onChange={() => toggleCpiType(type)} />
                {title.split(' — ')[0]}
              </label>
            ))}
          </div>
          <button className="btn btn-secondary" disabled={!cpiPct || !cpiTypes.length || store.status !== 'ok'}
            onClick={() => { if (onApplyCpi(cpiPct, cpiTypes)) setCpiPct(''); }}>
            Apply CPI
          </button>
          <button className="btn btn-ghost" disabled={!cpiUndos.length} onClick={onUndoCpi}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            title={cpiUndos.length ? `Undo the +${cpiUndos[cpiUndos.length - 1].pct}% increase` : 'Nothing to undo'}>
            <RotateCcw size={14} /> Undo{cpiUndos.length ? ` +${cpiUndos[cpiUndos.length - 1].pct}%` : ''}
          </button>
        </div>
      </div>

      {dirty && (
        <div className="card" style={{ marginBottom: 14, background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.45)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={16} style={{ color: '#f59e0b' }} />
          <span style={{ fontSize: '0.85rem' }}><strong>Unsaved changes.</strong> Invoices keep billing at the saved rates until you press Save rates.</span>
        </div>
      )}

      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14, marginBottom: 14 }}>
        {RATE_SECTIONS.map(({ type, title, hint }) => (
          <div key={type} className="card">
            <div style={{ fontWeight: 600, color: 'var(--hp-gold)', marginBottom: 2 }}>{title}</div>
            <div style={{ fontSize: '0.76rem', color: 'var(--hp-muted)', marginBottom: 10 }}>{hint} All ex GST.</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
              {RATE_FIELDS[type].map(({ key, label }) => {
                const saved = latest ? normaliseRates(latest.rates)[type][key] : null;
                const changed = saved != null && Number(editor.rates[type][key]) !== saved;
                return (
                  <Field key={key} label={label}>
                    <input className="form-input" type="number" step="0.0001" value={editor.rates[type][key]}
                      onChange={e => setRate(type, key, e.target.value)}
                      style={changed ? { borderColor: '#f59e0b' } : undefined}
                      title={changed ? `Saved: ${fmtRate(saved)}` : undefined} />
                  </Field>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="card card-gold" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: '0.85rem', color: saveMsg && saveMsg.startsWith('Error') ? 'var(--hp-danger)' : 'var(--hp-muted)' }}>
          {saveMsg || 'Saving adds a new version. Earlier versions are kept, so earlier periods keep their rates.'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onDiscard} disabled={!dirty || saving}>Discard changes</button>
          <button className="btn btn-primary" onClick={onSave} disabled={saving || store.status !== 'ok' || !editor.effectiveFrom}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save rates'}
          </button>
        </div>
      </div>

      {history.length > 0 && (
        <div className="card" style={{ marginTop: 14, overflowX: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 600 }}>Rate history</div>
            {prior && (
              <button className="btn btn-sm btn-ghost" onClick={onUndoSave} disabled={saving || store.status !== 'ok'}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                title="Saves the previous rates again for the same effective date. The mistaken version stays in the history.">
                <RotateCcw size={13} /> Undo last save
              </button>
            )}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(128,128,128,0.2)', textAlign: 'left' }}>
                <th style={{ padding: '4px 6px' }}>Effective from</th>
                <th style={{ padding: '4px 6px' }}>DoE day / school</th>
                <th style={{ padding: '4px 6px' }}>EP item / adj</th>
                <th style={{ padding: '4px 6px' }}>Night staff / leader</th>
                <th style={{ padding: '4px 6px' }}>Add. time staff / leader</th>
                <th style={{ padding: '4px 6px' }}>Saved</th>
                <th style={{ padding: '4px 6px' }}>Note</th>
              </tr>
            </thead>
            <tbody>
              {history.map(v => {
                const r = normaliseRates(v.rates);
                return (
                  <tr key={v.id} style={{ borderBottom: '1px solid rgba(128,128,128,0.1)' }}>
                    <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{formatMedium(v.effectiveFrom)}</td>
                    <td style={{ padding: '4px 6px' }}>{fmtRate(r.doe.mainRate)} / {fmtRate(r.doe.schoolRate)}</td>
                    <td style={{ padding: '4px 6px' }}>{fmtRate(r.wave.unitPrice)} / {fmtRate(r.wave.adjPerItem)}</td>
                    <td style={{ padding: '4px 6px' }}>{fmtRate(r.night.staff)} / {fmtRate(r.night.leader)}</td>
                    <td style={{ padding: '4px 6px' }}>{fmtRate(r.extra.staff)} / {fmtRate(r.extra.leader)}</td>
                    <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{fmtStamp(v.savedAt)}{v.savedBy ? ` · ${v.savedBy}` : ''}</td>
                    <td style={{ padding: '4px 6px', color: 'var(--hp-muted)' }}>{v.note}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'doe',   label: 'DoE', sub: 'Dept of Education' },
  { key: 'wave',  label: '2nd Wave EP', sub: 'Express Parcels' },
  { key: 'night', label: 'Night Sort', sub: 'Operations' },
  { key: 'extra', label: 'Additional Time', sub: 'Outside the window' },
  { key: 'rates', label: 'Rates', sub: 'Saved, with history' },
];

// Each invoice tab bills from the period START it holds (night/extra hold the true
// Sunday start; DoE/EP the Monday after — same period either way).
const editorFrom = (versions) => {
  const latest = latestVersion(versions);
  return {
    rates: normaliseRates(latest ? latest.rates : CURRENT_RATES),
    effectiveFrom: findCurrentPeriod().startISO,
    note: '',
  };
};
const editorNumbers = (rates) => {
  const out = {};
  RATE_TYPES.forEach(t => { out[t] = {}; RATE_FIELDS[t].forEach(({ key }) => { out[t][key] = Number(rates[t][key]); }); });
  return out;
};

export default function TaxInvoicesPage() {
  const [activeTab, setActiveTab] = useState('doe');
  const [doeForm,   setDoeForm]   = useState(makeDoeForm);
  const [waveForm,  setWaveForm]  = useState(makeWaveForm);
  const [nightForm, setNightForm] = useState(makeNightForm);
  const [extraForm, setExtraForm] = useState(makeExtraForm);

  // Saved rates — the only source any tab bills from.
  const [rateStore, setRateStore] = useState({ status: 'loading', versions: [], error: '' });
  const [editor, setEditor] = useState(() => editorFrom([]));
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [deviceRates, setDeviceRates] = useState(null);
  // CPI applies, newest last — each holds the editor as it was before, so Undo is exact.
  const [cpiUndos, setCpiUndos] = useState([]);

  const loadRates = () => {
    setRateStore(s => ({ ...s, status: 'loading', error: '' }));
    ratesApi('getRates')
      .then(d => {
        const versions = d.versions || [];
        setRateStore({ status: 'ok', versions, error: '' });
        setEditor(editorFrom(versions));
        setCpiUndos([]);
        // The old per-browser Night Sort copy never bills again. Identical → drop it
        // quietly; different → show it once on the Rates tab so nothing is lost unseen.
        let legacy = null;
        try { legacy = JSON.parse(localStorage.getItem(LEGACY_NIGHT_RATES_KEY) || 'null'); } catch {}
        if (legacy && typeof legacy === 'object') {
          const saved = ratesFor(versions, new Date().toISOString().slice(0, 10)).night;
          const differs = RATE_FIELDS.night.some(({ key }) => legacy[key] != null && Number(legacy[key]) !== saved[key]);
          if (differs) setDeviceRates(legacy);
          else try { localStorage.removeItem(LEGACY_NIGHT_RATES_KEY); } catch {}
        }
      })
      .catch(err => setRateStore(s => ({ ...s, status: 'error', error: err.message })));
  };
  useEffect(() => { loadRates(); }, []);

  const latest = latestVersion(rateStore.versions);
  const dirty = !!latest
    ? ratesDiffer(editorNumbers(editor.rates), normaliseRates(latest.rates)) || !!editor.note.trim()
    : rateStore.status === 'ok';

  // Leaving with unsaved rate edits asks first.
  useEffect(() => {
    if (!dirty || rateStore.status !== 'ok') return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty, rateStore.status]);

  const saveRates = async () => {
    const nums = editorNumbers(editor.rates);
    const bad = RATE_TYPES.flatMap(t => RATE_FIELDS[t].filter(({ key }) => editor.rates[t][key] === '' || !Number.isFinite(nums[t][key])).map(f => f.label));
    if (bad.length) { setSaveMsg(`Error: not a number — ${bad.join(', ')}`); return; }
    setSaving(true); setSaveMsg('');
    try {
      await ratesApi('saveRates', { rates: nums, effectiveFrom: editor.effectiveFrom, note: editor.note.trim() });
      // Read back what the server holds and show THAT — the save only counts once it's there.
      const d = await ratesApi('getRates');
      const versions = d.versions || [];
      setRateStore({ status: 'ok', versions, error: '' });
      setEditor(editorFrom(versions));
      setCpiUndos([]);
      setSaveMsg(`Saved ${fmtStamp(latestVersion(versions).savedAt)} — in force for periods from ${formatMedium(editor.effectiveFrom)}.`);
    } catch (err) {
      setSaveMsg(`Error: ${err.message} — nothing was saved.`);
    } finally { setSaving(false); }
  };

  const applyCpiIncrease = (pctText, types) => {
    const pct = Number(pctText);
    if (!Number.isFinite(pct) || pct === 0) { setSaveMsg('Error: enter the CPI increase as a percentage, e.g. 3.2'); return false; }
    if (Math.abs(pct) > 25 && !window.confirm(`${pct}% is a very large change. Apply it anyway?`)) return false;
    const nums = editorNumbers(editor.rates);
    if (RATE_TYPES.some(t => RATE_FIELDS[t].some(({ key }) => !Number.isFinite(nums[t][key])))) {
      setSaveMsg('Error: fix the blank or non-number rates before applying CPI.'); return false;
    }
    setCpiUndos(u => [...u, { pct, editor }]);
    setEditor(e => ({ ...e, rates: applyCpi(nums, pct, types), note: e.note.trim() ? e.note : `CPI +${pct}%` }));
    const n = types.reduce((a, t) => a + RATE_FIELDS[t].length, 0);
    setSaveMsg(`CPI +${pct}% applied to ${n} rates (${types.map(t => RATE_SECTIONS.find(x => x.type === t).title.split(' — ')[0]).join(', ')}). Not saved yet — check the figures and the effective-from date, then press Save rates.`);
    return true;
  };
  const undoCpi = () => {
    const last = cpiUndos[cpiUndos.length - 1];
    if (!last) return;
    setEditor(last.editor);
    setCpiUndos(u => u.slice(0, -1));
    setSaveMsg(`Undid the CPI +${last.pct}% increase.`);
  };

  // A wrong save is undone by saving the previous rates again for the same effective
  // date — append-only, so the mistaken version stays visible in the history.
  const undoLastSave = async () => {
    const prior = versionBefore(rateStore.versions);
    if (!latest || !prior) return;
    const msg = `Undo the save of ${fmtStamp(latest.savedAt)}?\n\nPeriods from ${formatMedium(latest.effectiveFrom)} go back to the rates in force before it`
      + ` (DoE ${fmtRate(normaliseRates(prior.rates).doe.mainRate)}/day).` + (dirty ? '\n\nYour unsaved edits in the editor will be discarded.' : '');
    if (!window.confirm(msg)) return;
    setSaving(true); setSaveMsg('');
    try {
      await ratesApi('saveRates', { rates: normaliseRates(prior.rates), effectiveFrom: latest.effectiveFrom,
        note: `Undo of the ${fmtStamp(latest.savedAt)} save` });
      const d = await ratesApi('getRates');
      const versions = d.versions || [];
      setRateStore({ status: 'ok', versions, error: '' });
      setEditor(editorFrom(versions));
      setCpiUndos([]);
      setSaveMsg(`Undone — periods from ${formatMedium(latest.effectiveFrom)} are back on the earlier rates. The undone version stays in the history.`);
    } catch (err) {
      setSaveMsg(`Error: ${err.message} — nothing was changed.`);
    } finally { setSaving(false); }
  };

  const useDeviceRates = () => {
    setEditor(e => {
      const pick = (t) => { const o = { ...e.rates[t] }; RATE_FIELDS[t].forEach(({ key }) => { if (deviceRates[key] != null) o[key] = Number(deviceRates[key]); }); return o; };
      return { ...e, rates: { ...e.rates, night: pick('night'), extra: pick('extra') } };
    });
    try { localStorage.removeItem(LEGACY_NIGHT_RATES_KEY); } catch {}
    setDeviceRates(null);
  };
  const dropDeviceRates = () => {
    try { localStorage.removeItem(LEGACY_NIGHT_RATES_KEY); } catch {}
    setDeviceRates(null);
  };

  // Rates + a one-line provenance note for a tab's period.
  const ratesForTab = (periodStart) => {
    const v = versionFor(rateStore.versions, periodStart);
    const rates = rateStore.status === 'ok' && v ? normaliseRates(v.rates) : normaliseRates(CURRENT_RATES);
    let note;
    if (rateStore.status === 'error' || (rateStore.status === 'ok' && !v)) {
      note = (
        <div className="card" style={{ marginBottom: 14, borderColor: 'var(--hp-danger)', background: 'rgba(248,113,113,0.08)', fontSize: '0.85rem' }}>
          <AlertTriangle size={14} style={{ color: 'var(--hp-danger)', verticalAlign: -2, marginRight: 6 }} />
          <strong style={{ color: 'var(--hp-danger)' }}>
            {rateStore.status === 'error' ? `Saved rates could not load (${rateStore.error}).` : 'No rates have been saved yet.'}
          </strong>{' '}
          Showing the built-in current rates — check them before printing.{' '}
          {rateStore.status === 'error' && <button className="btn btn-sm btn-secondary" onClick={loadRates}>Retry</button>}
        </div>
      );
    } else if (rateStore.status === 'loading') {
      note = <div style={{ fontSize: '0.8rem', color: 'var(--hp-muted)', marginBottom: 10 }}>Loading saved rates…</div>;
    } else {
      const isLatest = latest && v.id === latest.id;
      note = (
        <div style={{ fontSize: '0.8rem', color: isLatest ? 'var(--hp-muted)' : '#f59e0b', marginBottom: 10 }}>
          Rates: saved version in force from {formatMedium(v.effectiveFrom)}{isLatest ? '' : ' — an earlier rate, because this period started before the newest rates took effect'}.{' '}
          <button className="btn btn-sm btn-ghost" style={{ padding: '0 6px' }} onClick={() => setActiveTab('rates')}>Rates tab →</button>
        </div>
      );
    }
    return { rates, rateNote: note };
  };

  // Pre-fill the next sequential invoice number from the shared server counter
  // (fills any form whose number is still empty; typed numbers are never overwritten).
  useEffect(() => {
    fetch('/.netlify/functions/appsscript', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getNextInvoiceNumber' }),
    })
      .then(r => r.json())
      .then(d => {
        if (!d.success || !d.next) return;
        const n = String(d.next);
        setDoeForm(f => f.invoiceNum ? f : { ...f, invoiceNum: n });
        setWaveForm(f => f.invoiceNum ? f : { ...f, invoiceNum: n });
        setNightForm(f => f.invoiceNum ? f : { ...f, invoiceNum: n });
        setExtraForm(f => f.invoiceNum ? f : { ...f, invoiceNum: n });
      })
      .catch(() => {});
  }, []);

  const resetTab = () => {
    // Reset never brings back a code rate: invoice tabs re-read the saved rates, and
    // on the Rates tab it throws away unsaved edits back to the saved version.
    if (activeTab === 'rates') { setEditor(editorFrom(rateStore.versions)); setCpiUndos([]); setSaveMsg(''); return; }
    if (activeTab === 'doe')   setDoeForm(makeDoeForm());
    if (activeTab === 'wave')  setWaveForm(makeWaveForm());
    if (activeTab === 'night') setNightForm(makeNightForm());
    if (activeTab === 'extra') setExtraForm(makeExtraForm());
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileText size={22} style={{ color: 'var(--hp-gold)' }} />
            Tax Invoices
          </h2>
          <p>PH Bouveng Pty Ltd — Australia Post PVA invoices</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={resetTab} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RotateCcw size={14} /> Reset
          </button>
        </div>
      </div>

      <div className="page-body">
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid rgba(128,128,128,0.15)', paddingBottom: 0 }}>
          {TABS.map(({ key, label, sub }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: activeTab === key ? '2px solid var(--hp-gold)' : '2px solid transparent',
                marginBottom: -2,
                padding: '10px 20px',
                cursor: 'pointer',
                color: activeTab === key ? 'var(--hp-gold)' : 'var(--hp-muted)',
                fontWeight: activeTab === key ? 700 : 400,
                fontSize: '0.95rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <span>{label}</span>
              <span style={{ fontSize: '0.72rem', opacity: 0.7 }}>{sub}</span>
            </button>
          ))}
        </div>

        {activeTab === 'doe'   && <DoeTab   form={doeForm}   setForm={setDoeForm}   {...ratesForTab(doeForm.periodStart)} />}
        {activeTab === 'wave'  && <WaveTab  form={waveForm}  setForm={setWaveForm}  {...ratesForTab(waveForm.periodStart)} />}
        {activeTab === 'night' && <NightTab form={nightForm} setForm={setNightForm} {...ratesForTab(nightForm.periodStart)} />}
        {activeTab === 'extra' && <ExtraTab form={extraForm} setForm={setExtraForm} {...ratesForTab(extraForm.periodStart)} />}
        {activeTab === 'rates' && (
          <RatesTab store={rateStore} editor={editor} setEditor={setEditor} dirty={dirty} saving={saving} saveMsg={saveMsg}
            onSave={saveRates} onDiscard={() => { setEditor(editorFrom(rateStore.versions)); setCpiUndos([]); setSaveMsg(''); }} onRetry={loadRates}
            deviceRates={deviceRates} onUseDevice={useDeviceRates} onDropDevice={dropDeviceRates}
            onApplyCpi={applyCpiIncrease} onUndoCpi={undoCpi} cpiUndos={cpiUndos} onUndoSave={undoLastSave} />
        )}
      </div>
    </div>
  );
}
